import { talosBadgesLogoURL } from 'config/constants';
import { useSlackAuth } from 'features/auth/SlackAuthContext';
import {
  applyBattleCatalogSectionFreeze,
  badgeMatchesCatalogSearch,
  badgeName,
  badgeOf,
  badgeRarity,
  BATTLE_ACTIONS_PER_TURN,
  BATTLE_CATALOG_SECTIONS,
  BATTLE_DECK_SIZE,
  BATTLE_MAX_HAND,
  BATTLE_OPENING_ACTIONS,
  BATTLE_PURE_MODE_LABELS,
  battleActionPrompt,
  battleCatalogSectionFreeze,
  battleFirstPile,
  battlePileCount,
  battleRoomPath,
  canDraw,
  cappedStat,
  cardLegalPiles,
  cardStatGain,
  cardTotalStats,
  catalogClaimedSectionStats,
  catalogDeckArchetypes,
  catalogSectionClaimedOverflow,
  catalogSectionHasDraftRow,
  catalogSectionOwnedInOrder,
  catalogSectionPreview,
  catalogSectionsForBadge,
  compareBattleCatalogArchetypeOrder,
  compareBattleCatalogOrder,
  compareBattleCatalogRoleOrder,
  compareBattleCatalogSectionOrder,
  compareBattleCatalogStatOrder,
  compareBattleCatalogTowerOrder,
  compareBattleDeckOrder,
  CPU_DIFFICULTIES,
  CPU_DIFFICULTY_LABELS,
  CPU_OPENING_FACE,
  cpuBattleDeck,
  cpuDifficultyUnlockHint,
  cpuDrives,
  cpuEmoteDue,
  cpuEmoteHover,
  cpuLiveFace,
  cpuName,
  cpuShowsEmote,
  createBattle,
  CUSTOM_BATTLE_DECK,
  DECK_OF_THE_DAY,
  deckCurveNote,
  deckIdsMatch,
  deckLimitBlocking,
  deckLimitExceeded,
  deckPowerGames,
  deleteSavedBattleDeck,
  disabledPiles,
  effectsForBadge,
  effectShowsStatPill,
  effectTriggerBlocked,
  effectTriggerLabel,
  faceScore,
  forfeitBattle,
  formatBattleDeckCode,
  handPreviewRevealBoards,
  hasStatLead,
  hasTurnAction,
  hoverStatKeys,
  hoverStatLabel,
  isBattleBadge,
  isBattleCatalogStatSection,
  isCpuDifficultyUnlocked,
  isMyTurnToAct,
  isPileDisabled,
  isPileUnlocked,
  isTowerBadge,
  keepCpuEmote,
  knownSampleDeckPower,
  listSavedBattleDecks,
  matchesSampleBattleDeck,
  opponentOf,
  parseBattleDeckCode,
  parseBattleRoomId,
  pickCpuInput,
  PILE_MAX_RARITY,
  pileCard,
  pileUnlockRound,
  playablePiles,
  presentPiles,
  prestigeIsShielded,
  prestigeRaceIsClosing,
  prestigeToWin,
  raritiesAllowedOnPile,
  rateDeckPowerAsync,
  readBattleClaimedOnly,
  readBattleDeckSource,
  readCpuDifficulty,
  readYourBattleDeck,
  recommendBattleDeck,
  RECOMMENDED_DECK_MAX_ROLLS,
  RECOMMENDED_DECK_MIN_ROLLS,
  recommendedDeckGames,
  recommendedDeckMinWinRate,
  recordCpuDifficultyWin,
  reduceBattle,
  revealableCards,
  revealRarityIsBlocked,
  SAMPLE_BATTLE_DECKS,
  sampleBattleDeckById,
  saveBattleDeck,
  scheduledActionsLeft,
  shouldCpuAct,
  shownTurnStillOpen,
  splitCatalogSectionBadges,
  statLeadIsIgnored,
  totalStats,
  writeBattleClaimedOnly,
  writeBattleDeckSource,
  writeCpuDifficulty,
  writeYourBattleDeck,
  type BattleCatalogSectionId,
  type BattleDeckSource,
  type BattleInput,
  type BattleLogEntry,
  type BattleState,
  type CpuDifficulty,
  type EmoteFace,
  type PileIndex,
  type PlayerId,
  type SavedBattleDeck,
} from 'features/battle/index';
import { recordBattleWin } from 'features/battle/leaderboard-api';
import { BattleOpenBadgeProvider, useBattleOpenBadge } from 'features/battle/ui/BattleOpenBadge';
import { BattlePageHeader } from 'features/battle/ui/BattlePageHeader';
import { DeckGroupFill, DeckGroupSwatch, DeckRarityPips, DeckStats, sumDeckStats } from 'features/battle/ui/DeckStats';
import { useBattleRoom } from 'features/battle/useBattleRoom';
import { BADGE_STAT_KEYS, BADGE_STAT_LABELS, type BadgeStatKey } from 'features/catalog/badge-definitions';
import { SAVED_DECK_WASH_OPACITY, SAVED_DECK_WASH_OPACITY_LOADED } from 'features/catalog/badge-hash-gradient';
import { isBadgeReleased } from 'features/catalog/badge-release';
import { liveCatalogBadgeIDs } from 'features/catalog/badge-state';
import {
  boardStatTone,
  RARITY_ORDER,
  RARITY_TIER_LABELS,
  rarityNameForStatScore,
  rarityOrderIndex,
  type RarityName,
} from 'features/catalog/rarity';
import {
  BATTLE_ALLOW_UNCLAIMED,
  BATTLE_EFFECT_PREVIEW_LINES,
  DECK_LIMITS,
  deckPowerTone,
  type DeckPowerTone,
} from 'features/catalog/rarity-config';
import { Badge } from 'features/catalog/useBadges';
import React, { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { BadgeComponent } from 'shared/components/BadgeComponent';
import { HoverTip } from 'shared/components/HoverTip';
import { LoginBadgeBackdrop } from 'shared/components/LoginBadgeBackdrop';
import { GalleryGroupRarityCounts } from 'shared/components/RarityCountsDisplay';
import { useTalosData } from 'shared/context/TalosDataContext';
import { BATTLE_BADGES_PATH, BATTLE_PATH_SEGMENTS } from 'shared/lib/nav-paths';
import '../../gallery/BadgeGalleryView.css';
import './BadgesBattleView.css';
import {
  fxDoneMs,
  mergeShownFx,
  PILE_LABEL_FX,
  presentSteps,
  prestigeFxDelta,
  type BattleFxItem,
  type PresentStep,
  type TargetMark,
} from './present-fx';
import { catalogScrollParent, useCatalogSectionMove, useDelayedCatalogOrder } from './useCatalogSectionMove';
import { useRevealDrag } from './useRevealDrag';

export { BattleOpenBadgeProvider, useBattleOpenBadge } from 'features/battle/ui/BattleOpenBadge';
export { BattlePageHeader } from 'features/battle/ui/BattlePageHeader';
export { BattleViewportGate } from 'features/battle/ui/BattleViewport';
export { DeckGroupFill, DeckGroupSwatch, DeckStats, sumDeckStats } from 'features/battle/ui/DeckStats';

/** Common first, matching the old rarity menu. */
const CATALOG_RARITY_FILTERS = [...RARITY_ORDER].reverse();

const DECK_POWER_TONE_CLASS: Record<DeckPowerTone, string> = {
  poor: 'is-poor',
  fair: 'is-fair',
  good: 'is-good',
  great: 'is-great',
};

export type BattlePreset = {
  /** Builds the battle; called on mount and on restart. */
  create: (seed: number) => BattleState;
  difficulty: CpuDifficulty;
  /** Header kicker, e.g. "How to Play · Stat Lead" or "Arena · Game 2". */
  label: string;
  /** Header title. Defaults to Battle Badges. */
  title?: string;
  /** Header leave-button label when a match is in progress. */
  exitLabel?: string;
  leaveConfirm?: { title: string; body: string; confirm: string };
  /** Confirming leave during a live match forfeits as a loss instead of calling onExit. */
  forfeitOnLeave?: boolean;
  /** Floating coach card shown during the match (tutorial only). Gets the live battle. */
  coach?: (state: BattleState) => React.ReactNode;
  /** Inputs the viewer may make. Anything else is ignored, and only allowed targets are highlighted. */
  allowInput?: (state: BattleState, input: BattleInput) => boolean;
  /** Whether the coach is actively highlighting a scripted move on the board. */
  coaching?: (state: BattleState) => boolean;
  /** Tutorial: board stat the coach is talking about, so the stats pile can point at it. */
  coachStat?: (state: BattleState) => BadgeStatKey | null;
  /** Tutorial: highlight the Prestige boxes and action cue on the opening overview screen. */
  coachOverview?: (state: BattleState) => boolean;
  /** Replaces the CPU's own pick. `null` makes the CPU wait. */
  cpuInput?: (state: BattleState) => BattleInput | null;
  /** Wait before each CPU move, in place of the usual pacing. */
  cpuPauseMs?: (state: BattleState, defaultMs: number) => number;
  /** Replaces the Rematch / Change decks buttons in the result modal and header. */
  resultActions: (state: BattleState, restart: () => void) => React.ReactNode;
  /** Called once when the battle reaches game_over. */
  onGameOver?: (state: BattleState) => void;
  /** Keep the result modal closed (tutorial holds it until the last recap is read). */
  holdResult?: (state: BattleState) => boolean;
  /** Leave battle / exit, called after the normal leave cleanup. */
  onExit: () => void;
};

type Props = {
  playerName: string;
  playerId?: string;
  /** Sheet profile id used to record CPU wins. */
  profileId?: string | null;
  ownedBadgeIds: string[];
  preset?: BattlePreset;
  /** Opens the gallery badge modal. */
  onSelectBadge?: (badge: Badge) => void;
};

const PURE_DECK_HINT = 'Built only from claimed badges.';
const HAND_FULL_HINT = 'Your hand is full';
const ROOM_CREATE_ERROR = 'Could not create the challenge. Try again.';
const ROOM_JOIN_ERROR = 'Could not join the battle. Try again.';

const discardFanMetrics = (n: number) => {
  const count = Math.max(n, 0);
  const spread = Math.min(1.55, Math.max(0.9, 9.5 / Math.max(1, count - 1)));
  const twist = Math.min(4.5, Math.max(1.2, 18 / Math.max(count, 1)));
  return { count, spread, twist };
};

const discardCardStyle = (index: number, n: number): React.CSSProperties => {
  const { count, spread, twist } = discardFanMetrics(n);
  return {
    '--i': index,
    '--bb-discard-x': `${(index - count + 1) * spread}rem`,
    '--bb-discard-rot': `${(index - (count - 1) / 2) * twist}deg`,
  } as React.CSSProperties;
};

const foeCardStyle = (index: number, n: number): React.CSSProperties =>
  ({
    transform: `rotate(${(index - (n - 1) / 2) * 4}deg)`,
  }) as React.CSSProperties;

const ClaimedOnlyPill: React.FC<{ hintId: string }> = ({ hintId }) => (
  <span className="bb-claimed-pill" aria-describedby={hintId}>
    {BATTLE_PURE_MODE_LABELS.pure}
    <span className="bb-deck-pure-tip" id={hintId} role="tooltip">
      {PURE_DECK_HINT}
    </span>
  </span>
);

const DeckPowerMeta: React.FC<{
  percent: number | null;
  pending: boolean;
  difficulty: CpuDifficulty;
  disabled: boolean;
  onRate: () => void;
}> = ({ percent, pending, difficulty, disabled, onRate }) => {
  const unknown = difficulty === 'impossible';
  return (
    <button
      type="button"
      className={`bb-deck-power${!unknown && percent == null ? ' is-pending' : ''}`}
      disabled={disabled || unknown}
      aria-busy={pending && percent == null}
      aria-label={
        unknown
          ? 'Deck power unknown vs Impossible CPU'
          : percent == null
            ? 'Rate deck power'
            : `Deck power ${percent}%`
      }
      onClick={onRate}
    >
      <span
        className={`bb-deck-power-value${
          unknown
            ? ' bb-win-rate-unknown'
            : percent != null && !pending
              ? ` ${DECK_POWER_TONE_CLASS[deckPowerTone(percent)]}`
              : ''
        }`}
      >
        {unknown ? '??%' : percent == null ? '...' : `${percent}%`}
      </span>{' '}
      vs <span className={`bb-cpu-difficulty is-${difficulty}`}>{CPU_DIFFICULTY_LABELS[difficulty]}</span> CPU
    </button>
  );
};
const PURE_LOBBY_HINT = 'Claimed Only lobby: Only decks with claimed badges can be picked.';
const PURE_DECK_REQUIRED_HINT = 'Claimed Only needs every badge in your deck claimed';
const BattleField = React.memo(LoginBadgeBackdrop);

const CPU_PAUSE_MS: Record<BattleLogEntry['kind'], number> = {
  play: 2000,
  reveal: 1400,
  draw: 1100,
  score: 1600,
  round: 800,
  info: 1200,
};

/** Wait before the first action of a CPU turn. The opening uses this on its own; later turns add it to the usual pause. */
const CPU_TURN_START_PAUSE_MS = 2000;
/** After Randomize Deck settles, rarity borders flash this long, then fade. */
const RANDOM_FILL_FLASH_MS = 1000;

/** How long a replayed opponent move stays up before the next one, same pacing as the CPU. */
const replayPauseMs = (state: BattleState): number => CPU_PAUSE_MS[state.log.at(-1)?.kind ?? 'info'];

/** Turn cue for the viewer, calling out when these are the last actions of the whole battle. */
const humanActionCue = (thisTurn: number, thisBattle: number, opens: boolean): string => {
  if (opens) return `You go first, take ${thisTurn} action${thisTurn === 1 ? '' : 's'}`;
  if (!Number.isFinite(thisBattle)) return `${thisTurn} action${thisTurn === 1 ? '' : 's'} left`;
  if (thisBattle === 1) return 'Last action this battle';
  if (thisBattle <= thisTurn) return `Last ${thisBattle} actions`;
  return `${thisTurn} action${thisTurn === 1 ? '' : 's'} left`;
};

/** Prestige at which a player is reminded that reaching 10 ends the battle on the spot. */
const INSTANT_WIN_HINT_PRESTIGE = 6;

const loggedLines = (entry: BattleLogEntry): string[] => [entry.text, ...(entry.details ?? [])];

const prestigeAmount = (line: string, name: string, verb: 'gained' | 'lost' | 'stole'): number | null => {
  const prefix = `${name} ${verb} `;
  if (!line.startsWith(prefix)) return null;
  const match = line.slice(prefix.length).match(/^(\d+) Prestige(?: from .+)?\.$/);
  if (!match) return null;
  const amount = Number(match[1]);
  return Number.isFinite(amount) ? amount : null;
};

/** True when this seat was already ahead as the turn that just ended began. */
const leadHeld = (state: BattleState, player: PlayerId): boolean => {
  const foe = opponentOf(player);
  const mine = state.players[player].name;
  const theirs = state.players[foe].name;
  let minePrestige = 0;
  let foePrestige = 0;
  let aheadAtTurnStart = false;
  state.log.forEach((entry) => {
    loggedLines(entry).forEach((line) => {
      if (/^Turn \d+\.$/.test(line)) {
        aheadAtTurnStart = minePrestige > foePrestige;
        return;
      }
      const gained = prestigeAmount(line, mine, 'gained');
      const lost = prestigeAmount(line, mine, 'lost');
      const stole = prestigeAmount(line, mine, 'stole');
      const foeGained = prestigeAmount(line, theirs, 'gained');
      const foeLost = prestigeAmount(line, theirs, 'lost');
      const foeStole = prestigeAmount(line, theirs, 'stole');
      if (gained != null) minePrestige += gained;
      if (lost != null) minePrestige -= lost;
      if (stole != null) {
        minePrestige += stole;
        foePrestige -= stole;
      }
      if (foeGained != null) foePrestige += foeGained;
      if (foeLost != null) foePrestige -= foeLost;
      if (foeStole != null) {
        foePrestige += foeStole;
        minePrestige -= foeStole;
      }
    });
  });
  return aheadAtTurnStart;
};

const cpuEmoteLine = (
  state: BattleState,
  player: PlayerId,
  shown: { face: EmoteFace; emoji: string } | null
): { face: EmoteFace; emoji: string } => {
  const opening =
    state.phase !== 'game_over' &&
    state.round === 1 &&
    state.players.every((seat) => seat.prestige === 0 && seat.actionsTaken === 0);
  const end =
    state.phase !== 'game_over' ? null : state.winner === 'tie' ? 'tie' : state.winner === player ? 'win' : 'loss';
  const face: EmoteFace = opening ? CPU_OPENING_FACE : (end ?? cpuLiveFace(state, player, leadHeld(state, player)));
  const roll = opening
    ? Math.abs(state.rng) + player
    : state.round * 5 + player * 3 + state.players[player].prestige + state.log.length;
  return { face, emoji: keepCpuEmote(face, roll, shown) };
};

const useHeldCpuEmotes = (
  state: BattleState | null,
  live: BattleState | null
): readonly [{ emoji: string; face: EmoteFace } | null, { emoji: string; face: EmoteFace } | null] => {
  const shownRef = useRef<[{ emoji: string; face: EmoteFace } | null, { emoji: string; face: EmoteFace } | null]>([
    null,
    null,
  ]);
  const [shown, setShown] = useState<
    [{ emoji: string; face: EmoteFace } | null, { emoji: string; face: EmoteFace } | null]
  >([null, null]);

  const commit = (player: PlayerId, next: { emoji: string; face: EmoteFace } | null) => {
    const prev = shownRef.current[player];
    if (prev?.emoji === next?.emoji && prev?.face === next?.face) return;
    shownRef.current[player] = next;
    setShown([shownRef.current[0], shownRef.current[1]]);
  };

  useLayoutEffect(() => {
    ([0, 1] as const).forEach((player) => {
      if (!state || !cpuShowsEmote(state, player)) {
        commit(player, null);
        return;
      }
      // A choice or a half-played effect is still this action. A stepped board has not landed yet.
      const settled = state.pending == null && state.stack.length === 0;
      const caughtUp = live == null || state === live;
      if (!cpuEmoteDue(shownRef.current[player] != null, settled, caughtUp)) return;
      commit(player, cpuEmoteLine(state, player, shownRef.current[player]));
    });
  }, [live, state]);

  return shown;
};

const isOpeningPlay = (state: BattleState): boolean =>
  state.phase === 'turn' &&
  state.round === 1 &&
  state.turnsTakenThisRound === 0 &&
  state.actionsLeft ===
    (state.openingPlayer === state.activePlayer ? BATTLE_OPENING_ACTIONS : BATTLE_ACTIONS_PER_TURN) &&
  state.pending == null;

const opponentOpens = (state: BattleState, viewing: PlayerId): boolean =>
  isOpeningPlay(state) && state.activePlayer !== viewing;

const viewerOpens = (state: BattleState, viewing: PlayerId): boolean =>
  isOpeningPlay(state) && state.activePlayer === viewing;

const cpuPauseMs = (state: BattleState, viewing: PlayerId): number => {
  if (opponentOpens(state, viewing)) return CPU_TURN_START_PAUSE_MS;
  const pause = CPU_PAUSE_MS[state.log.at(-1)?.kind ?? 'info'];
  const openingItsTurn =
    state.phase === 'turn' &&
    state.pending == null &&
    cpuDrives(state, state.activePlayer) &&
    state.players[state.activePlayer].actionsTaken === 0;
  return openingItsTurn ? pause + CPU_TURN_START_PAUSE_MS : pause;
};

const activeFullscreen = (): Element | null => {
  const webkit = (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ?? null;
  return document.fullscreenElement ?? webkit;
};

const exitBattleFullscreen = (force = false) => {
  const active = activeFullscreen();
  if (!force && !active?.classList.contains('bb-page')) return;
  if (!active && !force) return;
  const webkitExit = (document as Document & { webkitExitFullscreen?: () => void }).webkitExitFullscreen;
  void Promise.resolve(document.exitFullscreen?.() ?? webkitExit?.()).catch(() => undefined);
};

const freezeShellBackdrop = () => {
  document.documentElement.classList.add('bb-match-fullscreen');
};

const releaseShellBackdrop = () => {
  document.documentElement.classList.remove('bb-match-fullscreen');
};

const enterBattleFullscreen = (page: HTMLElement) => {
  // Freeze the shell field before the browser resizes, so it never rebuilds to fullscreen size.
  freezeShellBackdrop();
  if (activeFullscreen() === page) return;
  const webkitRequest = (page as HTMLElement & { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen;
  const dropIfDenied = () => {
    if (!activeFullscreen()?.classList.contains('bb-page')) releaseShellBackdrop();
  };
  try {
    const pending = page.requestFullscreen?.() ?? webkitRequest?.call(page);
    if (pending && typeof (pending as Promise<void>).catch === 'function') {
      void (pending as Promise<void>).catch(dropIfDenied);
    }
  } catch {
    webkitRequest?.call(page);
  }
};

const projectBoardStats = (
  state: BattleState,
  current: Record<BadgeStatKey, number>,
  revealId: string,
  replaceId: string | null
): Record<BadgeStatKey, number> => {
  const add = cardTotalStats(state, revealId);
  const remove = replaceId ? cardTotalStats(state, replaceId) : null;
  const next = { ...current };
  for (const key of BADGE_STAT_KEYS) {
    next[key] = current[key] + add[key] - (remove?.[key] ?? 0);
  }
  return next;
};

const LaneStats: React.FC<{
  mine: Record<BadgeStatKey, number>;
  theirs: Record<BadgeStatKey, number>;
  preview?: Record<BadgeStatKey, number> | null;
  focusStat?: BadgeStatKey | null;
}> = ({ mine, theirs, preview, focusStat }) => {
  const shown = preview ?? mine;
  return (
    <div className="bb-pile bb-stat-pile" aria-label="Board stats">
      <span className="bb-pile-pips bb-stat-pile-label">Stats</span>
      <div className="bb-pile-stack bb-stat-bars">
        {BADGE_STAT_KEYS.map((key) => {
          const current = mine[key];
          const next = shown[key];
          const peak = Math.max(1, current, next, theirs[key]);
          const delta = next - current;
          const ahead = next > theirs[key];
          const held = next > 0;
          const kept = Math.min(current, next);
          const shift = `${(kept / peak) * 100}%`;
          const rarity = boardStatTone(next, theirs[key]);
          const coached = focusStat === key;
          return (
            <div
              key={key}
              className={`bb-hover-stat${ahead ? ' is-ahead' : held ? ' is-held' : ' is-behind'}${
                delta > 0 ? ' is-gain' : delta < 0 ? ' is-loss' : ''
              }${coached ? ' is-coached' : ''}`}
            >
              {coached ? <span className="bb-stat-coach-arrow" aria-hidden /> : null}
              <span className={`bb-hover-stat-name rarity-${rarity}`}>{BADGE_STAT_LABELS[key]}</span>
              <span className="bb-hover-stat-track" aria-hidden>
                <span className={`bb-hover-stat-fill rarity-${rarity}`} style={{ width: shift }} />
                {delta > 0 ? (
                  <span
                    className="bb-hover-stat-delta is-gain"
                    style={{ left: shift, width: `${(delta / peak) * 100}%` }}
                  />
                ) : null}
                {delta < 0 ? (
                  <span
                    className="bb-hover-stat-delta is-loss"
                    style={{ left: shift, width: `${(-delta / peak) * 100}%` }}
                  />
                ) : null}
              </span>
              <b className={`bb-hover-stat-num stat-score-num rarity-${rarity}`}>{next}</b>
            </div>
          );
        })}
      </div>
    </div>
  );
};

type ChainLinkSpec = { x: number; y: number; angle: number; cross: boolean; i: number };

const chainAlong = (x1: number, y1: number, x2: number, y2: number, count: number, startI: number): ChainLinkSpec[] => {
  const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
  return Array.from({ length: count }, (_, i) => ({
    x: x1 + ((x2 - x1) * i) / (count - 1),
    y: y1 + ((y2 - y1) * i) / (count - 1),
    angle,
    cross: i % 2 === 1,
    i: startI + i,
  }));
};

const DISABLED_CHAIN_LINKS = [...chainAlong(18, 22, 102, 138, 8, 0), ...chainAlong(102, 22, 18, 138, 8, 8)];

const PileDisableLasso: React.FC<{ releasing?: boolean }> = ({ releasing }) => (
  <div className={`bb-pile-chains${releasing ? ' is-releasing' : ''}`} aria-hidden>
    <svg className="bb-pile-chains-svg" viewBox="0 0 120 160">
      {DISABLED_CHAIN_LINKS.map((link) => (
        <ellipse
          key={link.i}
          className="bb-pile-chain-link"
          cx={link.x}
          cy={link.y}
          rx={link.cross ? 4.5 : 8.2}
          ry={link.cross ? 8.2 : 4.5}
          style={{ '--i': link.i, '--r': `${link.angle}deg` } as React.CSSProperties}
        />
      ))}
    </svg>
  </div>
);

const PRESTIGE_LOCK_CORNERS = ['tl', 'tr', 'bl', 'br'] as const;

const PrestigeShieldLocks: React.FC = () => (
  <div className="bb-prestige-locks" aria-hidden>
    <svg className="bb-prestige-locks-ring" viewBox="0 0 100 72" preserveAspectRatio="none">
      <rect className="bb-prestige-locks-band" x="4" y="4" width="92" height="64" rx="10" />
    </svg>
    {PRESTIGE_LOCK_CORNERS.map((corner) => (
      <svg key={corner} className={`bb-prestige-lock is-${corner}`} viewBox="0 0 20 24">
        <path className="bb-prestige-lock-shackle" d="M5.5 10.2V7.2a4.5 4.5 0 0 1 9 0v3" />
        <rect className="bb-prestige-lock-body" x="3" y="10" width="14" height="11" rx="2.2" />
        <circle className="bb-prestige-lock-key" cx="10" cy="15.2" r="1.15" />
      </svg>
    ))}
  </div>
);

const PileRarityPips: React.FC<{
  pile: PileIndex;
  unlocked: boolean;
  blockedRarities?: ReadonlySet<RarityName>;
}> = ({ pile, unlocked, blockedRarities }) => {
  if (!unlocked) {
    return <span className="bb-pile-lock" aria-hidden />;
  }
  const allowed = raritiesAllowedOnPile(pile).filter((rarity) => !blockedRarities?.has(rarity));
  const allowedSet = new Set(allowed);
  const top = allowed[allowed.length - 1] ?? PILE_MAX_RARITY[pile];
  const label = `Up to ${RARITY_TIER_LABELS[top]}`;
  return (
    <HoverTip text={label} className="is-stretch">
      <span className="bb-pile-pips" aria-label={label}>
        {PILE_MAX_RARITY.map((rarity) => (
          <span key={rarity} className={`bb-pile-pip rarity-${rarity}${allowedSet.has(rarity) ? ' is-on' : ''}`} />
        ))}
      </span>
    </HoverTip>
  );
};

const BattleHoverBody: React.FC<{
  badge: Badge;
  myBoard?: Record<BadgeStatKey, number>;
  foeBoard?: Record<BadgeStatKey, number>;
  /** Hand cards: my board totals with each pile this badge could occupy once every pile is unlocked. */
  revealBoards?: readonly Record<BadgeStatKey, number>[];
  playHint?: string | null;
  hideEffects?: boolean;
  /** Extra stats added for a while, e.g. SRT Distributooor's +5. */
  statBoost?: Partial<Record<BadgeStatKey, number>> | null;
  /** Stats added on top of the printed ones while this badge is on the board. */
  statGain?: Partial<Record<BadgeStatKey, number>> | null;
  /** Catalog stat dropdown: lead with this axis so towers show the filtered stat first. */
  preferStat?: BadgeStatKey;
  /** Board On Reveal: whether that reveal already fired. Hand, deck, and catalog leave this unset. */
  onRevealFired?: boolean;
  /** Leading Ongoing `ignore_stat_lead` on the board unlocks Activate triggers without a stat lead. */
  ignoreStatLead?: boolean;
}> = ({
  badge,
  myBoard,
  foeBoard,
  revealBoards,
  playHint,
  hideEffects,
  statBoost,
  statGain,
  preferStat,
  onRevealFired,
  ignoreStatLead,
}) => {
  const shownStats: Record<BadgeStatKey, number> = { ...badge.stats };
  if (statBoost) {
    for (const key of BADGE_STAT_KEYS) shownStats[key] += statBoost[key] ?? 0;
  }
  if (statGain) {
    for (const key of BADGE_STAT_KEYS) shownStats[key] += statGain[key] ?? 0;
  }
  for (const key of BADGE_STAT_KEYS) shownStats[key] = cappedStat(shownStats[key]);
  const effectStatValue = (stat?: BadgeStatKey) => (stat ? shownStats[stat] : -1);
  const effectStatIndex = (stat?: BadgeStatKey) => (stat ? BADGE_STAT_KEYS.indexOf(stat) : BADGE_STAT_KEYS.length);
  const effects = hideEffects
    ? []
    : [...effectsForBadge(badge.id)].sort(
        (a, b) => effectStatValue(b.stat) - effectStatValue(a.stat) || effectStatIndex(a.stat) - effectStatIndex(b.stat)
      );
  const pillStatKeys = new Set(
    effects.flatMap((effect) =>
      effect.stat && effectShowsStatPill(effect.trigger, onRevealFired) ? [effect.stat] : []
    )
  );
  const orderedStats = hoverStatKeys(shownStats, [...pillStatKeys], preferStat);
  /** Best total I can reach in `stat`, placing this badge on whichever legal pile does most. */
  const mineFor = (stat: BadgeStatKey, board: Record<BadgeStatKey, number>): number =>
    revealBoards?.length ? Math.max(...revealBoards.map((b) => b[stat] + shownStats[stat])) : board[stat];
  const leadsStat = (stat: BadgeStatKey): boolean => {
    if (!myBoard || !foeBoard) return true;
    return mineFor(stat, myBoard) > foeBoard[stat];
  };
  return (
    <div className="bb-hover">
      {orderedStats.length ? (
        <div className="bb-hover-stats">
          {orderedStats.map((key, index) => {
            const value = shownStats[key];
            const isEffect = pillStatKeys.has(key);
            const rarity = isEffect ? badge.rarityName : rarityNameForStatScore(value);
            const compared = isEffect && myBoard != null && foeBoard != null && !ignoreStatLead;
            const leading = compared && leadsStat(key);
            const tied = compared && mineFor(key, myBoard!) === foeBoard![key];
            const blocked = compared && !leading;
            const fullLabel = BADGE_STAT_LABELS[key];
            const label = hoverStatLabel(key, index, orderedStats.length);
            return (
              <div
                key={key}
                className={`bb-hover-stat${isEffect ? ' is-effect' : ''}`}
                title={label === fullLabel ? undefined : fullLabel}
              >
                {isEffect ? (
                  <span
                    className={`bb-hover-effect-stat rarity-${rarity}${blocked ? ' is-blocked' : ''}${tied ? ' is-tied' : ''}`}
                  >
                    {label}
                    <b>{value}</b>
                  </span>
                ) : (
                  <span className={`bb-hover-stat-plain rarity-${rarity}`}>
                    {label}
                    <b>{value}</b>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      {effects.map((effect, index) => {
        const leading = Boolean(ignoreStatLead) || (effect.stat ? leadsStat(effect.stat) : true);
        const blocked = effectTriggerBlocked(effect.trigger, leading, onRevealFired);
        const triggerLabel = effectTriggerLabel(effect.trigger, onRevealFired);
        return (
          <p key={`${effect.stat ?? 'text'}-${effect.text}`} className="bb-hover-effect">
            <span className={`bb-hover-effect-text${effect.ops.length === 0 ? ' has-note' : ''}`}>
              {triggerLabel ? (
                <>
                  <b className={blocked ? 'is-blocked' : undefined}>{triggerLabel}:</b> {effect.text}
                </>
              ) : (
                effect.text
              )}
            </span>
            {playHint && index === effects.length - 1 ? <span className="bb-hover-hint">{playHint}</span> : null}
          </p>
        );
      })}
      {!effects.length && playHint ? (
        <p className="bb-hover-effect">
          <span className="bb-hover-effect-text">{playHint}</span>
        </p>
      ) : null}
    </div>
  );
};

type EffectProbe = { tile: HTMLElement; probe: HTMLElement; lineHeight: number };

/**
 * Measures whether each tile's effect runs past the unhovered preview, so hover keeps those
 * lines on the bottom of the image. Mount measures flush before paint, including while the
 * pointer is already over a card just played onto a pile. Resize measures stay batched.
 */
const pendingEffectMeasures = new Map<HTMLElement, (overflows: boolean) => void>();
let effectMeasureFrame = 0;
let effectSettleTimer = 0;
let effectMeasureSync = false;

const flushEffectMeasures = () => {
  effectMeasureFrame = 0;
  const lines = BATTLE_EFFECT_PREVIEW_LINES;
  const jobs = [...pendingEffectMeasures];
  pendingEffectMeasures.clear();
  const results = new Map<HTMLElement, boolean>();
  const toProbe: { tile: HTMLElement; effect: HTMLElement; parent: HTMLElement; width: number; lineHeight: number }[] =
    [];

  // Read phase. Hover padding and the 1.04 scale must not change the result: a card played
  // under the cursor is already hovered, and it still has to use the resting preview width.
  for (const [tile] of jobs) {
    if (!tile.isConnected) continue;
    results.set(tile, false);
    for (const effect of tile.querySelectorAll<HTMLElement>('.bb-hover-effect')) {
      const text = effect.querySelector('.bb-hover-effect-text')?.textContent?.trim() ?? '';
      if (!text) continue;
      if (lines <= 0) {
        results.set(tile, true);
        break;
      }
      const style = getComputedStyle(effect);
      const lineHeight = parseFloat(style.lineHeight);
      const padX = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const width = effect.clientWidth - padX;
      const parent = effect.parentElement;
      if (!lineHeight || width <= 0 || !parent) continue;
      toProbe.push({ tile, effect, parent, width, lineHeight });
    }
  }

  // Write phase.
  const probes: EffectProbe[] = toProbe.map(({ tile, effect, parent, width, lineHeight }) => {
    // Clone the pill and the sentence. Measuring the sentence alone misses wraps the pill causes.
    const probe = effect.cloneNode(true) as HTMLElement;
    probe.setAttribute('aria-hidden', 'true');
    probe.style.position = 'absolute';
    probe.style.left = '0';
    probe.style.right = 'auto';
    probe.style.top = '0';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.display = 'block';
    probe.style.boxSizing = 'content-box';
    probe.style.overflow = 'visible';
    probe.style.height = 'auto';
    probe.style.maxHeight = 'none';
    probe.style.maxWidth = 'none';
    probe.style.width = `${width}px`;
    probe.style.padding = '0';
    probe.style.margin = '0';
    probe.style.transform = 'none';
    probe.style.lineHeight = `${lineHeight}px`;
    probe.style.setProperty('-webkit-line-clamp', 'unset');
    probe.style.setProperty('line-clamp', 'unset');
    parent.appendChild(probe);
    return { tile, probe, lineHeight };
  });

  // Single layout, then read every probe. offsetHeight ignores the hover scale, which
  // getBoundingClientRect includes and which would mark a resting 2-line effect as overflow.
  for (const { tile, probe, lineHeight } of probes) {
    if (probe.offsetHeight / lineHeight > lines + 0.15) results.set(tile, true);
  }
  for (const { probe } of probes) probe.remove();

  for (const [tile, done] of jobs) {
    const overflows = results.get(tile);
    if (overflows != null && tile.isConnected) done(overflows);
  }
};

const scheduleEffectMeasure = (tile: HTMLElement, done: (overflows: boolean) => void) => {
  pendingEffectMeasures.set(tile, done);
  if (effectSettleTimer) return;
  if (effectMeasureFrame) {
    cancelAnimationFrame(effectMeasureFrame);
    effectMeasureFrame = 0;
  }
  // One flush for every tile mounted in this commit, before paint, so a card played
  // under the cursor is already bottom-aligned on the first frame.
  if (effectMeasureSync) return;
  effectMeasureSync = true;
  queueMicrotask(() => {
    effectMeasureSync = false;
    if (!pendingEffectMeasures.size) return;
    flushSync(() => {
      flushEffectMeasures();
    });
  });
};

/** Resizes arrive every frame while the window animates (e.g. leaving fullscreen). Measure once it settles. */
const EFFECT_RESIZE_SETTLE_MS = 150;

const scheduleEffectMeasureAfterResize = (tile: HTMLElement, done: (overflows: boolean) => void) => {
  pendingEffectMeasures.set(tile, done);
  if (effectMeasureFrame) {
    cancelAnimationFrame(effectMeasureFrame);
    effectMeasureFrame = 0;
  }
  window.clearTimeout(effectSettleTimer);
  effectSettleTimer = window.setTimeout(() => {
    effectSettleTimer = 0;
    effectMeasureFrame = requestAnimationFrame(flushEffectMeasures);
  }, EFFECT_RESIZE_SETTLE_MS);
};

/** One observer for every tile, instead of one per tile. */
const tileResizeHandlers = new WeakMap<Element, () => void>();
let tileResizeObserver: ResizeObserver | null = null;

const observeTileResize = (tile: HTMLElement, onResize: () => void) => {
  tileResizeObserver ??= new ResizeObserver((entries) => {
    for (const entry of entries) tileResizeHandlers.get(entry.target)?.();
  });
  // ResizeObserver always fires once on observe; the mount measure already covers it.
  let initial = true;
  tileResizeHandlers.set(tile, () => {
    if (initial) {
      initial = false;
      return;
    }
    onResize();
  });
  tileResizeObserver.observe(tile);
  return () => {
    tileResizeObserver?.unobserve(tile);
    tileResizeHandlers.delete(tile);
  };
};

type BattleBadgeProps = {
  badge: Badge;
  dimmed?: boolean;
  selected?: boolean;
  legal?: boolean;
  /** Picking this badge costs you it (discard, bottom of deck, give away), so it's highlighted in red. */
  danger?: boolean;
  /** The tutorial wants this badge clicked next. */
  coached?: boolean;
  /** Settled Randomize Deck fills: flash the rarity border, then it fades. */
  randomFill?: boolean;
  locked?: boolean;
  grayscale?: boolean;
  /** Board, discard, and hand cards: no floating name, so the tile is just the image. */
  compact?: boolean;
  /** Catalog overlay, e.g. "Already in deck" or "Unclaimed". Stays up on hover, above the stats. */
  veil?: string | null;
  myBoard?: Record<BadgeStatKey, number>;
  foeBoard?: Record<BadgeStatKey, number>;
  revealBoards?: readonly Record<BadgeStatKey, number>[];
  playHint?: string | null;
  hideEffects?: boolean;
  statBoost?: Partial<Record<BadgeStatKey, number>> | null;
  statGain?: Partial<Record<BadgeStatKey, number>> | null;
  /** Receives the badge, so lists can pass one stable handler and keep the memo effective. */
  onClick?: (badge: Badge) => void;
  /** Tiny image pill when this player has not claimed the badge. */
  pill?: 'unclaimed by you';
  /** Arena draft deal-in order. Omit to skip the entrance animation. */
  dealIndex?: number;
  /** Catalog stat dropdown: lead hover stats with this axis. */
  preferStat?: BadgeStatKey;
  /** Board On Reveal: whether that reveal already fired. Hand, deck, and catalog leave this unset. */
  onRevealFired?: boolean;
  /** Disabled pile: image only, no hover stats, effect text, or pills. */
  imageOnly?: boolean;
  /** Board Ongoing that is currently leading its stat. */
  ongoing?: boolean;
  /** Leading Ongoing `ignore_stat_lead` on the board unlocks Activate triggers without a stat lead. */
  ignoreStatLead?: boolean;
  /** Catalog FLIP id. Same slot → no move animation. */
  catalogBadge?: string;
};

const BattleBadgeTile: React.FC<BattleBadgeProps> = ({
  badge,
  dimmed,
  selected,
  legal,
  danger,
  coached,
  randomFill,
  locked,
  grayscale,
  compact,
  veil,
  myBoard,
  foeBoard,
  revealBoards,
  playHint,
  hideEffects,
  statBoost,
  statGain,
  onClick,
  pill,
  dealIndex,
  preferStat,
  onRevealFired,
  imageOnly,
  ongoing,
  ignoreStatLead,
  catalogBadge,
}) => {
  const tileRef = useRef<HTMLDivElement>(null);
  const [effectOverflow, setEffectOverflow] = useState(false);
  const tower = isTowerBadge(badge.id);

  useLayoutEffect(() => {
    if (imageOnly) return;
    const tile = tileRef.current;
    if (!tile) return;
    const apply = (next: boolean) => setEffectOverflow((current) => (current === next ? current : next));
    scheduleEffectMeasure(tile, apply);
    const unobserve = observeTileResize(tile, () => scheduleEffectMeasureAfterResize(tile, apply));
    return () => {
      pendingEffectMeasures.delete(tile);
      unobserve();
    };
  }, [badge.id, compact, hideEffects, playHint, imageOnly]);

  return (
    <div
      ref={tileRef}
      data-bb-catalog-badge={catalogBadge}
      title={randomFill ? 'Randomized fill' : undefined}
      className={`gallery-badge bb-tile rarity-${badge.rarityName}${compact ? ' is-compact' : ''}${
        effectOverflow ? ' is-effect-overflow' : ''
      }${
        legal ? ` is-legal${danger ? ' is-danger' : ''}` : ''
      }${coached ? ' is-coached' : ''}${randomFill ? ' is-random-fill' : ''}${selected ? ' is-selected' : ''}${dimmed ? ' is-dim' : ''}${
        locked ? ' is-locked' : ''
      }${veil ? ' is-pinned' : ''}${tower ? ' is-tower' : ''}${imageOnly ? ' is-image-only' : ''}${
        veil === 'Unclaimed' ? ' is-unclaimed' : ''
      }${ongoing ? ' is-ongoing' : ''}${onClick ? '' : ' is-unclickable'}${dealIndex != null ? ' bb-arena-deal' : ''}`}
      style={dealIndex != null ? ({ '--bb-deal-i': dealIndex } as React.CSSProperties) : undefined}
    >
      <BadgeComponent
        badge={badge}
        onClick={() => onClick?.(badge)}
        blackAndWhite={!!dimmed || !!locked || !!grayscale}
        lowOpacity={!!dimmed}
        cursorNotAllowed={!onClick}
        display
        smaller
        showName={!compact && !imageOnly}
        showFloatingNameShadow={false}
        showDescription={false}
        hoverContentAlways={!imageOnly}
        imageOverlayLabel={imageOnly ? null : (veil ?? null)}
        cornerPill={imageOnly ? null : pill}
        hoverContent={
          imageOnly ? null : (
            <BattleHoverBody
              badge={badge}
              myBoard={myBoard}
              foeBoard={foeBoard}
              revealBoards={revealBoards}
              playHint={playHint}
              hideEffects={hideEffects}
              statBoost={statBoost}
              statGain={statGain}
              preferStat={preferStat}
              onRevealFired={onRevealFired}
              ignoreStatLead={ignoreStatLead}
            />
          )
        }
      />
    </div>
  );
};

export const BattleBadge = React.memo(BattleBadgeTile);

const catalogMoreNoun = (count: number, hiddenLabel: string): string =>
  count === 1 && hiddenLabel === 'badges' ? 'badge' : hiddenLabel;

const CatalogMoreTile: React.FC<{
  count: number;
  expanded: boolean;
  hiddenLabel?: string;
  onToggle: () => void;
}> = ({ count, expanded, hiddenLabel = 'badges', onToggle }) => {
  if (count <= 0) return null;
  const noun = catalogMoreNoun(count, hiddenLabel);
  return (
    <div
      className={`gallery-badge bb-catalog-more${expanded ? ' is-expanded' : ''}`}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={`${count} more ${noun}. ${expanded ? 'Collapse' : 'Expand'} to ${expanded ? 'hide' : 'review'}.`}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          onToggle();
        }
      }}
    >
      <span className="gallery-badge-hidden-tile">
        <span className="gallery-badge-hidden-count">+{count}</span>
        <span className="gallery-badge-hidden-label">{expanded ? 'hide' : noun}</span>
      </span>
    </div>
  );
};

const BattleCardBack: React.FC = () => (
  <div className="bb-card-back" aria-hidden>
    <img src={talosBadgesLogoURL} alt="" />
  </div>
);

/** Hand picks that cost the picker the badge. */
const LOSING_HAND_PICKS = new Set(['discard', 'bottom', 'give']);
/** Picks of your own board badge that take it off your board. */
const LOSING_BOARD_PICKS = new Set(['bounce_self', 'discard_self']);

const battleFxLabel = (item: BattleFxItem): string => {
  if (item.revealedAndActivated) return 'Revealed with Effect';
  if (item.kind === 'play' || item.activated) return 'Activated';
  if (item.kind === 'reveal') return 'Revealed';
  if (item.kind === 'upgrade') return 'Upgraded';
  if (item.kind === 'shuffle') return 'Shuffled in';
  if (item.kind === 'swap') return 'Swapped';
  if (item.kind === 'remove_pile') return 'Removed';
  if (item.kind === 'draw') return 'Drew';
  if (item.kind === 'discard') return 'Discarded';
  if (item.kind === 'exchange') return 'Exchanged';
  return item.to === 'deck' ? 'To deck' : 'To hand';
};

const BattleFxCluster: React.FC<{
  items: BattleFxItem[];
  zone: 'hand' | 'discard' | 'board' | 'deck';
  viewing: PlayerId;
  badgesMap: Map<string, Badge>;
  onDone: (key: string) => void;
}> = ({ items, zone, viewing, badgesMap, onDone }) => {
  if (!items.length) return null;
  return (
    <div className={`bb-fx-cluster is-${zone}`} aria-live="polite">
      {items.map((item) => {
        const hideCard = (item.kind === 'draw' && item.player !== viewing) || item.kind === 'shuffle';
        const badge = hideCard ? undefined : badgesMap.get(item.badgeId);
        if (!hideCard && !badge) return null;
        return (
          <div
            key={item.key}
            className={`bb-fx-item is-${item.kind}${item.to ? ` is-to-${item.to}` : ''}${
              item.activated ? ' is-activated' : ''
            } ${item.player === viewing ? 'is-mine' : 'is-foe'}`}
            onAnimationEnd={(event) => {
              if (event.target !== event.currentTarget) return;
              onDone(item.key);
            }}
          >
            <span className="bb-fx-label">{battleFxLabel(item)}</span>
            {badge ? <BattleBadge badge={badge} grayscale={item.kind === 'discard'} compact /> : <BattleCardBack />}
          </div>
        );
      })}
    </div>
  );
};

const BattleLogEffectLine: React.FC<{
  cardId?: string;
  stat?: BadgeStatKey;
  trigger?: BattleLogEntry['trigger'];
  effectText: string;
  cards?: BattleState['cards'];
  sourceName?: string;
}> = ({ cardId, stat, trigger, effectText, cards, sourceName }) => {
  const rarity = cardId ? badgeRarity(badgeOf({ cards }, cardId)) : undefined;
  return (
    <p className="bb-log-effect">
      {sourceName ? `${sourceName} — ` : null}
      {trigger && trigger !== 'On Reveal' && stat ? (
        <span className={`bb-hover-effect-stat${rarity ? ` rarity-${rarity}` : ''}`}>{BADGE_STAT_LABELS[stat]}</span>
      ) : null}
      {trigger ? (
        <>
          <b>{trigger}:</b> {effectText}
        </>
      ) : (
        effectText
      )}
    </p>
  );
};

const BattleLogList: React.FC<{ entries: BattleLogEntry[]; cards?: BattleState['cards'] }> = ({ entries, cards }) => (
  <ol>
    {[...entries].reverse().map((entry, index) => (
      <li key={`${entry.kind}-${entry.text}-${index}`} className={`bb-log-item is-${entry.kind}`}>
        <p className="bb-log-head">{entry.text}</p>
        {entry.kind === 'play' && entry.stat && entry.effectText ? (
          <BattleLogEffectLine
            cardId={entry.cardId}
            stat={entry.stat}
            trigger={entry.trigger}
            effectText={entry.effectText}
            cards={cards}
          />
        ) : null}
        {entry.causedBy?.map((item, causedIndex) => (
          <BattleLogEffectLine
            key={`${item.cardId}-${causedIndex}`}
            cardId={item.cardId}
            stat={item.stat}
            trigger={item.trigger}
            effectText={item.text}
            cards={cards}
            sourceName={badgeName(badgeOf({ cards }, item.cardId))}
          />
        ))}
        {entry.details?.map((detail, detailIndex) => (
          <p key={detailIndex} className="bb-log-detail">
            {detail}
          </p>
        ))}
      </li>
    ))}
  </ol>
);

const compareBattleDeck = (a: Badge, b: Badge): number => compareBattleDeckOrder(a, b);

const battleCatalogTitle = (rarity: RarityName | 'all'): string => {
  if (rarity === 'all') return 'Battle Badges';
  return `${RARITY_TIER_LABELS[rarity]} Badges`;
};

const BadgesBattleScreen: React.FC<Props> = ({
  playerName,
  playerId: playerIdProp,
  profileId,
  ownedBadgeIds,
  preset,
}) => {
  const onOpenBadge = useBattleOpenBadge();
  const { badgesMap } = useTalosData();
  const { user } = useSlackAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  const roomId = preset ? null : parseBattleRoomId(location.pathname);
  const knownBattlePage = BATTLE_PATH_SEGMENTS.some((seg) => seg === pathParts[1]);
  const invalidRoom = !preset && pathParts[0] === 'battle' && Boolean(pathParts[1]) && !roomId && !knownBattlePage;
  const playerId = playerIdProp || user?.email || user?.id || playerName;
  const {
    room,
    committed,
    status,
    seat,
    isFull,
    canJoin,
    createChallenge,
    joinAsGuest,
    commitState,
    rematch,
    abandon,
  } = useBattleRoom(roomId, playerId, playerName, replayPauseMs);
  const [claimedOnly, setClaimedOnly] = useState(readBattleClaimedOnly);
  const allowUnclaimedBadges = BATTLE_ALLOW_UNCLAIMED && !claimedOnly;
  const allowUnclaimed = allowUnclaimedBadges && !room?.pureOnlyBattle;
  const requirePure = !allowUnclaimed;
  const playableIds = useMemo(() => {
    const claimed = new Set(liveCatalogBadgeIDs(ownedBadgeIds, badgesMap));
    return [...badgesMap.values()]
      .filter((badge) => (allowUnclaimed || claimed.has(badge.id)) && isBattleBadge(badge.id))
      .sort((a, b) => rarityOrderIndex(b.rarityName) - rarityOrderIndex(a.rarityName) || a.name.localeCompare(b.name))
      .map((badge) => badge.id);
  }, [allowUnclaimed, badgesMap, ownedBadgeIds]);
  const claimedSet = useMemo(() => new Set(liveCatalogBadgeIDs(ownedBadgeIds, badgesMap)), [badgesMap, ownedBadgeIds]);
  const ownedSet = useMemo(() => new Set(playableIds), [playableIds]);
  const isUnclaimedBadge = (badgeId: string) => !claimedSet.has(badgeId);
  const [yourDeckMemory] = useState(readYourBattleDeck);
  const [deckSourceMemory] = useState(readBattleDeckSource);
  const restoredSample = deckSourceMemory === CUSTOM_BATTLE_DECK ? null : sampleBattleDeckById(deckSourceMemory);
  const [deck, setDeck] = useState<string[]>(() =>
    restoredSample ? [...restoredSample.badgeIds] : (yourDeckMemory ?? [])
  );
  const [deckSource, setDeckSource] = useState<BattleDeckSource>(deckSourceMemory);
  const customDeckRef = useRef<string[]>(yourDeckMemory ?? []);
  const customRandomizedRef = useRef<string[]>([]);
  const [rarityFilter, setRarityFilter] = useState<RarityName | 'all'>('all');
  const [catalogSearch, setCatalogSearch] = useState('');
  const catalogSearchRef = useRef<HTMLInputElement>(null);
  const catalogBucketsRef = useRef<HTMLDivElement>(null);
  const [openCatalogSection, setOpenCatalogSection] = useState<BattleCatalogSectionId | null>(null);
  const [openCatalogUnclaimed, setOpenCatalogUnclaimed] = useState<BattleCatalogSectionId | null>(null);
  const [catalogSectionFreeze, setCatalogSectionFreeze] = useState<readonly BattleCatalogSectionId[]>([]);
  const catalogSectionOrderRef = useRef<readonly BattleCatalogSectionId[]>([]);
  const [cpuState, setCpuState] = useState<BattleState | null>(() =>
    preset ? preset.create(Date.now() % 1_000_000 || 1) : null
  );
  const [cpuDifficulty, setCpuDifficulty] = useState<CpuDifficulty>(preset?.difficulty ?? readCpuDifficulty);
  const matchDifficultyRef = useRef<CpuDifficulty>(cpuDifficulty);
  const matchSeedRef = useRef(0);
  const recordedGameRef = useRef<BattleState | null>(null);
  const [, setGameOverEpoch] = useState(0);
  /** Difficulty the CPU plays a seat at. */
  const seatDifficulty = useCallback(
    (battle: BattleState, seat: PlayerId): CpuDifficulty => {
      if (seat === battle.cpu) return cpuDifficulty;
      return battle.autopilot?.[seat] ?? 'easy';
    },
    [cpuDifficulty]
  );
  const [difficultyMenuOpen, setDifficultyMenuOpen] = useState(false);
  const difficultyMenuRef = useRef<HTMLDivElement>(null);
  const [revealPick, setRevealPick] = useState<string | null>(null);
  const [hoverRevealPile, setHoverRevealPile] = useState<PileIndex | null>(null);
  const [fxItems, setFxItems] = useState<BattleFxItem[]>([]);
  const [targetFx, setTargetFx] = useState<TargetMark[]>([]);
  const [logOpen, setLogOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [roomActionError, setRoomActionError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [savedDecks, setSavedDecks] = useState<SavedBattleDeck[]>(() => listSavedBattleDecks());
  const loadedSavedDeck =
    deckSource === CUSTOM_BATTLE_DECK ? savedDecks.find((saved) => deckIdsMatch(saved.badgeIds, deck)) : undefined;
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [randomizedIds, setRandomizedIds] = useState<string[]>([]);
  const [randomFillFlash, setRandomFillFlash] = useState(false);
  const randomFillFlashTimer = useRef(0);
  const [deckToDelete, setDeckToDelete] = useState<SavedBattleDeck | null>(null);
  const [deckPower, setDeckPower] = useState<{ key: string; percent: number } | null>(null);
  const [ratingDeck, setRatingDeck] = useState(false);
  const [rollingRandom, setRollingRandom] = useState(false);
  const [copiedDeckCode, setCopiedDeckCode] = useState<string | null>(null);
  const fxSeq = useRef(0);
  const fxTimers = useRef<number[]>([]);
  const didSeedDeck = useRef(yourDeckMemory != null || restoredSample != null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const openMatchFullscreen = useRef(false);
  const leaveAfterFullscreen = useRef(false);
  const leaveToken = useRef(0);
  const scheduleDeckReturnRef = useRef<() => void>(() => {});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [holdBackdrop, setHoldBackdrop] = useState(false);
  const bindBattlePage = useCallback((node: HTMLDivElement | null) => {
    pageRef.current = node;
    if (!node) {
      setIsFullscreen(false);
      if (!openMatchFullscreen.current) exitBattleFullscreen(true);
      return;
    }
    if (activeFullscreen() === node) setIsFullscreen(true);
  }, []);
  /** The real battle. `state` below is what's on screen, which trails it while an opponent's move plays out. */
  const liveState = roomId ? (room?.state ?? null) : cpuState;
  const [shownState, setShownState] = useState<BattleState | null>(null);
  const [presenting, setPresenting] = useState(false);
  const state = liveState ? (shownState ?? liveState) : null;
  const [leavingPiles, setLeavingPiles] = useState<PileIndex[]>([]);
  const [releasingPiles, setReleasingPiles] = useState<PileIndex[]>([]);
  const prevFirstPile = useRef(0);
  const prevDisabledPiles = useRef<PileIndex[]>([]);
  useEffect(() => {
    if (!state) {
      setLeavingPiles([]);
      setReleasingPiles([]);
      prevFirstPile.current = 0;
      prevDisabledPiles.current = [];
      return;
    }
    const next = battleFirstPile(state);
    if (next > prevFirstPile.current) {
      const extra: PileIndex[] = [];
      for (let i = prevFirstPile.current; i < next; i++) extra.push(i as PileIndex);
      setLeavingPiles((current) => [...current, ...extra.filter((pile) => !current.includes(pile))]);
    } else if (next < prevFirstPile.current) {
      setLeavingPiles([]);
    }
    prevFirstPile.current = next;
    const locked = disabledPiles(state);
    const present = new Set(presentPiles(state));
    const freed = prevDisabledPiles.current.filter((pile) => !locked.includes(pile) && present.has(pile));
    setReleasingPiles((current) => {
      const kept = current.filter((pile) => !locked.includes(pile));
      return freed.length ? [...kept, ...freed.filter((pile) => !kept.includes(pile))] : kept;
    });
    prevDisabledPiles.current = locked;
  }, [state]);
  const heldCpuEmotes = useHeldCpuEmotes(state, liveState);
  const liveStateRef = useRef(liveState);
  liveStateRef.current = liveState;
  /** The state the viewer's own last input was applied to, so the result shows at once. */
  const ownInputFrom = useRef<BattleState | null>(null);
  const [hideResult, setHideResult] = useState(false);
  const viewing: PlayerId = seat ?? 0;
  const waitingForGuest = Boolean(room && seat === 0 && !room.state);
  const sampleDeck = sampleBattleDeckById(deckSource);
  const usingSample = sampleDeck != null;
  const deckTitle = sampleDeck?.name ?? loadedSavedDeck?.name ?? 'Your Deck';
  const claimPill = (badgeId: string): 'unclaimed by you' | undefined =>
    isUnclaimedBadge(badgeId) ? 'unclaimed by you' : undefined;
  // Randomize Deck is rolling: the deck keeps changing, so every other deck action waits.
  const editsLocked = waitingForGuest || rollingRandom;
  const lockSampleEdits = !BATTLE_ALLOW_UNCLAIMED && usingSample;
  const shareUrl = room ? `${window.location.origin}${battleRoomPath(room.id)}` : '';

  const apply = (input: BattleInput) => {
    // Only blocked while the screen still trails the real battle; the final step of a move is already live.
    if (shownState && shownState !== liveState) return;
    if (liveState && preset?.allowInput && !preset.allowInput(liveState, input)) return;
    setRevealPick(null);
    ownInputFrom.current = liveStateRef.current;
    if (roomId) {
      commitState(
        (current) =>
          isMyTurnToAct(current, viewing) && !cpuDrives(current, viewing) ? reduceBattle(current, input) : current,
        input
      );
      return;
    }
    setCpuState((current) => {
      if (!current) return current;
      if (shouldCpuAct(current)) return current;
      return reduceBattle(current, input);
    });
  };
  const applyRef = useRef(apply);
  applyRef.current = apply;

  const revealDrag = useRevealDrag({
    armed:
      !!state &&
      state.phase === 'turn' &&
      isMyTurnToAct(state, viewing) &&
      !cpuDrives(state, viewing) &&
      !state.pending &&
      !(shownState && shownState !== liveState),
    legalPiles: (cardId) => {
      if (!state) return [];
      const option = revealableCards(state).find((item) => item.cardId === cardId);
      if (!option) return [];
      return option.piles.filter(
        (pile) => !preset?.allowInput || preset.allowInput(state, { type: 'reveal', cardId, pile })
      );
    },
    onSelect: (cardId) => {
      setHoverRevealPile(null);
      setRevealPick(cardId);
    },
    onHoverPile: setHoverRevealPile,
    onDrop: (cardId, pile) => {
      if (shownState && shownState !== liveState) return false;
      if (state && preset?.allowInput && !preset.allowInput(state, { type: 'reveal', cardId, pile })) return false;
      apply({ type: 'reveal', cardId, pile });
      setHoverRevealPile(null);
      return true;
    },
    onCancel: () => {
      setHoverRevealPile(null);
      setRevealPick(null);
    },
  });

  useEffect(() => {
    if (isFullscreen || holdBackdrop || activeFullscreen()?.classList.contains('bb-page')) {
      freezeShellBackdrop();
      return;
    }
    releaseShellBackdrop();
  }, [holdBackdrop, isFullscreen]);

  useEffect(() => () => releaseShellBackdrop(), []);

  useEffect(() => {
    if (state || !holdBackdrop) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setHoldBackdrop(false));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [holdBackdrop, state]);

  useEffect(() => {
    if (!leaveAfterFullscreen.current || isFullscreen) return;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      if (cancelled || !leaveAfterFullscreen.current) return;
      if (!activeFullscreen()?.classList.contains('bb-page')) {
        scheduleDeckReturnRef.current();
        return;
      }
      exitBattleFullscreen(true);
    });
    const fallback = window.setTimeout(() => {
      if (cancelled || !leaveAfterFullscreen.current) return;
      if (activeFullscreen()?.classList.contains('bb-page')) exitBattleFullscreen(true);
      requestAnimationFrame(() => {
        if (!cancelled) scheduleDeckReturnRef.current();
      });
    }, 700);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.clearTimeout(fallback);
    };
  }, [isFullscreen]);

  useEffect(() => {
    const syncFullscreen = () => {
      const page = pageRef.current;
      const on = Boolean(page) && activeFullscreen() === page;
      if (leaveAfterFullscreen.current) {
        if (!on) scheduleDeckReturnRef.current();
        return;
      }
      setIsFullscreen(on);
    };
    const syncAfterEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      requestAnimationFrame(syncFullscreen);
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);
    window.addEventListener('keyup', syncAfterEscape);
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncFullscreen);
      window.removeEventListener('keyup', syncAfterEscape);
    };
  }, []);

  useEffect(() => {
    const resultOpen = state?.phase === 'game_over' && !hideResult && !preset?.holdResult?.(liveState ?? state);
    if (!logOpen && !saveOpen && !deckToDelete && !leaveConfirmOpen && !resultOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setLogOpen(false);
      setSaveOpen(false);
      setDeckToDelete(null);
      setLeaveConfirmOpen(false);
      if (resultOpen) setHideResult(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deckToDelete, hideResult, leaveConfirmOpen, liveState, logOpen, preset, saveOpen, state]);

  useEffect(() => {
    // Online, each seat's own client drives it once an effect handed that seat to the CPU.
    // Waits for the last move to finish playing, so a CPU move never starts over one still animating.
    if (presenting || !roomId || !liveState || !cpuDrives(liveState, viewing) || !isMyTurnToAct(liveState, viewing))
      return;
    const timer = window.setTimeout(
      () => {
        const input =
          pickCpuInput(liveState, liveState.autopilot?.[viewing] ?? 'easy') ??
          (hasTurnAction(liveState) ? null : ({ type: 'pass' } as const));
        if (!input) return;
        commitState((current) => (isMyTurnToAct(current, viewing) ? reduceBattle(current, input) : current), input);
      },
      cpuPauseMs(liveState, viewing)
    );
    return () => window.clearTimeout(timer);
  }, [commitState, liveState, presenting, roomId, viewing]);

  const presetCpuInput = preset?.cpuInput;
  const presetCpuPauseMs = preset?.cpuPauseMs;
  useEffect(() => {
    if (presenting || roomId || !liveState || !shouldCpuAct(liveState)) return;
    if (presetCpuInput && !presetCpuInput(liveState)) return;
    const defaultPauseMs = cpuPauseMs(liveState, viewing);
    const timer = window.setTimeout(
      () => {
        setCpuState((current) => {
          if (!current || !shouldCpuAct(current)) return current;
          const actor = current.pending?.player ?? current.activePlayer;
          const input = presetCpuInput
            ? presetCpuInput(current)
            : pickCpuInput(current, seatDifficulty(current, actor));
          if (input) return reduceBattle(current, input);
          if (current.phase === 'turn' && !current.pending && current.actionsLeft > 0 && !hasTurnAction(current)) {
            return reduceBattle(current, { type: 'pass' });
          }
          return current;
        });
      },
      presetCpuPauseMs ? presetCpuPauseMs(liveState, defaultPauseMs) : defaultPauseMs
    );
    return () => window.clearTimeout(timer);
  }, [cpuDifficulty, liveState, presenting, presetCpuInput, presetCpuPauseMs, roomId, seatDifficulty, viewing]);

  useEffect(() => {
    const live = liveState;
    if (
      presenting ||
      !live ||
      cpuDrives(live, viewing) ||
      live.phase !== 'turn' ||
      live.pending ||
      live.actionsLeft <= 0
    )
      return;
    if (!isMyTurnToAct(live, viewing) || hasTurnAction(live)) return;
    applyRef.current({ type: 'pass' });
  }, [liveState, presenting, viewing]);

  const playableBadges = useMemo(
    () => playableIds.map((id) => badgesMap.get(id)).filter((badge): badge is Badge => Boolean(badge)),
    [badgesMap, playableIds]
  );

  useEffect(() => {
    setDeck((current) => {
      if (!badgesMap.size) return current;
      const next = current.filter((id) => isBattleBadge(id) && badgesMap.has(id)).slice(0, BATTLE_DECK_SIZE);
      if (!didSeedDeck.current && next.length === 0 && playableBadges.length >= BATTLE_DECK_SIZE) {
        didSeedDeck.current = true;
        const saved = listSavedBattleDecks()[0];
        if (saved) {
          const savedIds = saved.badgeIds.filter((id) => ownedSet.has(id)).slice(0, BATTLE_DECK_SIZE);
          if (savedIds.length === BATTLE_DECK_SIZE) return savedIds;
        }
      }
      if (next.length) didSeedDeck.current = true;
      if (next.length === current.length && next.every((id, index) => id === current[index])) return current;
      return next;
    });
  }, [badgesMap, ownedSet, playableBadges]);

  useEffect(() => {
    if (deckSource !== CUSTOM_BATTLE_DECK) return;
    customDeckRef.current = deck;
    customRandomizedRef.current = randomizedIds;
    if (!didSeedDeck.current && deck.length === 0) return;
    didSeedDeck.current = true;
    writeYourBattleDeck(deck);
  }, [deck, deckSource, randomizedIds]);

  useEffect(() => {
    setRandomizedIds((current) => {
      const next = current.filter((id) => deck.includes(id));
      return next.length === current.length ? current : next;
    });
  }, [deck]);

  const stopRandomFillFlash = () => {
    window.clearTimeout(randomFillFlashTimer.current);
    randomFillFlashTimer.current = 0;
    setRandomFillFlash(false);
  };

  const startRandomFillFlash = () => {
    window.clearTimeout(randomFillFlashTimer.current);
    setRandomFillFlash(true);
    randomFillFlashTimer.current = window.setTimeout(() => {
      randomFillFlashTimer.current = 0;
      setRandomFillFlash(false);
    }, RANDOM_FILL_FLASH_MS);
  };

  useEffect(() => () => window.clearTimeout(randomFillFlashTimer.current), []);

  useEffect(() => {
    writeBattleDeckSource(deckSource);
  }, [deckSource]);

  const deckCards = useMemo(
    () => deck.map((id) => badgesMap.get(id)).filter((badge): badge is Badge => Boolean(badge)),
    [badgesMap, deck]
  );
  /** Keep the library sorted against the last settled deck while Randomize is still rolling. */
  const catalogSortDeckRef = useRef(deckCards);
  if (!rollingRandom) catalogSortDeckRef.current = deckCards;
  const catalogSortDeckCards = rollingRandom ? catalogSortDeckRef.current : deckCards;
  const catalogSortDeckIds = useMemo(
    () => new Set(catalogSortDeckCards.map((card) => card.id)),
    [catalogSortDeckCards]
  );

  const catalog = useMemo(() => {
    const matchesFilters = (badge: Badge) => {
      if (!badgeMatchesCatalogSearch(catalogSearch, badge)) return false;
      if (rarityFilter !== 'all' && badge.rarityName !== rarityFilter) return false;
      return true;
    };
    const owned: Badge[] = [];
    const unclaimed: Badge[] = [];
    for (const badge of badgesMap.values()) {
      if (!isBadgeReleased(badge.id) || !isBattleBadge(badge.id) || !matchesFilters(badge)) continue;
      if (claimedSet.has(badge.id)) owned.push(badge);
      else unclaimed.push(badge);
    }
    const byCatalogOrder = (a: Badge, b: Badge) => compareBattleCatalogOrder(a, b, 'all');
    owned.sort(byCatalogOrder);
    unclaimed.sort(byCatalogOrder);
    return {
      owned,
      unclaimed,
      items: allowUnclaimed ? [...owned, ...unclaimed].sort(byCatalogOrder) : [...owned, ...unclaimed],
    };
  }, [allowUnclaimed, badgesMap, catalogSearch, claimedSet, rarityFilter]);

  const battleCatalogTally = useMemo(() => {
    let owned = 0;
    let total = 0;
    for (const badge of badgesMap.values()) {
      if (!isBadgeReleased(badge.id) || !isBattleBadge(badge.id)) continue;
      total += 1;
      if (claimedSet.has(badge.id)) owned += 1;
    }
    return { owned, total };
  }, [badgesMap, claimedSet]);
  const canDraftCatalog = battleCatalogTally.owned >= BATTLE_DECK_SIZE;

  const catalogBuckets = useMemo(() => {
    const byId = new Map<BattleCatalogSectionId, Badge[]>(BATTLE_CATALOG_SECTIONS.map((section) => [section.id, []]));
    for (const badge of catalog.items) {
      for (const id of catalogSectionsForBadge(badge.id)) {
        byId.get(id)?.push(badge);
      }
    }
    const totals = sumDeckStats(catalogSortDeckCards);
    const claimedSectionStats = catalogClaimedSectionStats(catalog.items, claimedSet, catalogSortDeckIds);
    const archetypes = catalogDeckArchetypes(totals);
    const liveIds = BATTLE_CATALOG_SECTIONS.map((section) => section.id)
      .filter((id) => {
        const badges = byId.get(id) ?? [];
        const { owned } = catalogSectionOwnedInOrder(badges, claimedSet, catalogSortDeckIds);
        return catalogSectionHasDraftRow(id, owned.length, badges.length);
      })
      .sort((a, b) => compareBattleCatalogSectionOrder(a, b, totals, claimedSectionStats));
    const deckEmpty = catalogSortDeckIds.size === 0;
    const displayIds = applyBattleCatalogSectionFreeze(liveIds, deckEmpty ? [] : catalogSectionFreeze);
    const live = BATTLE_CATALOG_SECTIONS.flatMap((section) => {
      const badges = byId.get(section.id) ?? [];
      const { inDeck, claimed, unclaimed } = splitCatalogSectionBadges(badges, claimedSet, catalogSortDeckIds);
      if (!catalogSectionHasDraftRow(section.id, inDeck.length + claimed.length, badges.length)) return [];
      const compare = (a: Badge, b: Badge) => {
        if (section.id === 'towers') return compareBattleCatalogTowerOrder(a, b, totals);
        if (isBattleCatalogStatSection(section.id)) return compareBattleCatalogStatOrder(a, b, section.id);
        if (section.id === 'prestige' || section.id === 'finishers') {
          return compareBattleCatalogRoleOrder(a, b, archetypes, totals, section.id);
        }
        return compareBattleCatalogArchetypeOrder(a, b, archetypes, totals);
      };
      inDeck.sort(compareBattleDeck);
      claimed.sort(compare);
      unclaimed.sort(compare);
      return [{ ...section, badges: [...inDeck, ...claimed, ...unclaimed] }];
    });
    const byLiveId = new Map(live.map((section) => [section.id, section]));
    return displayIds.flatMap((id) => {
      const section = byLiveId.get(id);
      return section ? [section] : [];
    });
  }, [catalog.items, catalogSectionFreeze, claimedSet, catalogSortDeckCards, catalogSortDeckIds]);
  catalogSectionOrderRef.current = catalogBuckets.map((section) => section.id);
  const catalogMoveKey = catalogBuckets
    .map((section) => `${section.id}:${section.badges.map((badge) => badge.id).join(',')}`)
    .join('|');
  const catalogScrollTopRef = useRef<number | null>(null);
  const pinCatalogScroll = () => {
    const scroller = catalogScrollParent(catalogBucketsRef.current);
    if (scroller) catalogScrollTopRef.current = scroller.scrollTop;
  };
  const displayBuckets = useDelayedCatalogOrder(catalogBuckets, catalogMoveKey, pinCatalogScroll);
  const displayMoveKey = displayBuckets
    .map((section) => `${section.id}:${section.badges.map((badge) => badge.id).join(',')}`)
    .join('|');
  useCatalogSectionMove(
    catalogBucketsRef,
    displayMoveKey,
    `${openCatalogSection ?? ''}:${openCatalogUnclaimed ?? ''}`,
    catalogScrollTopRef
  );

  useEffect(() => {
    setOpenCatalogSection(null);
    setOpenCatalogUnclaimed(null);
    setCatalogSectionFreeze([]);
  }, [catalogSearch, rarityFilter]);

  const toggleCatalogSection = useCallback((id: BattleCatalogSectionId) => {
    setOpenCatalogSection((current) => (current === id ? null : id));
    setOpenCatalogUnclaimed(null);
  }, []);

  const toggleCatalogUnclaimed = useCallback((id: BattleCatalogSectionId) => {
    setOpenCatalogUnclaimed((current) => (current === id ? null : id));
  }, []);

  useEffect(() => {
    if (!difficultyMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!difficultyMenuRef.current?.contains(event.target as Node)) setDifficultyMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDifficultyMenuOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [difficultyMenuOpen]);

  const startMatch = () => {
    if (deck.length !== BATTLE_DECK_SIZE) return;
    const claimedOk = playableIds.length >= BATTLE_DECK_SIZE && deck.every((id) => ownedSet.has(id));
    if (!claimedOk && !matchesSampleBattleDeck(deck)) return;
    const seed = Date.now() % 1_000_000;
    matchSeedRef.current = seed;
    matchDifficultyRef.current = cpuDifficulty;
    const page = pageRef.current;
    const keepFullscreen = Boolean(page) && activeFullscreen() === page;
    openMatchFullscreen.current = keepFullscreen;
    flushSync(() => {
      setCpuState(
        createBattle({
          decks: [deck, cpuBattleDeck(seed)],
          names: [playerName, cpuName(cpuDifficulty)],
          seed,
          cpu: 1,
          cpuDifficulty,
          coinToss: true,
          // CPU decks are generated samples — never claimed-only.
          pure: [deck.every((id) => claimedSet.has(id)), false],
        })
      );
      setRevealPick(null);
      setFxItems([]);
      setLogOpen(false);
    });
    const nextPage = pageRef.current;
    if (keepFullscreen && nextPage && nextPage !== page && activeFullscreen() !== nextPage) {
      enterBattleFullscreen(nextPage);
    }
    openMatchFullscreen.current = false;
  };

  const restartPreset = () => {
    if (!preset) return;
    recordedGameRef.current = null;
    setHideResult(false);
    setRevealPick(null);
    setFxItems([]);
    const seed = Date.now() % 1_000_000 || 1;
    matchSeedRef.current = seed;
    setCpuState(preset.create(seed));
  };

  const finishLeaveRef = useRef(() => {});
  finishLeaveRef.current = () => {
    setRevealPick(null);
    setFxItems([]);
    setLogOpen(false);
    setLeaveConfirmOpen(false);
    setHideResult(false);
    setIsFullscreen(false);
    setCpuState(null);
    if (preset) {
      preset.onExit();
      return;
    }
    if (roomId) {
      abandon();
      navigate(BATTLE_BADGES_PATH);
    }
  };
  const scheduleDeckReturn = useCallback(() => {
    if (!leaveAfterFullscreen.current) return;
    if (activeFullscreen()?.classList.contains('bb-page')) return;
    leaveAfterFullscreen.current = false;
    const token = ++leaveToken.current;
    // Let the fullscreen resize paint before the deck builder mounts.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (leaveToken.current !== token) return;
        startTransition(() => finishLeaveRef.current());
      });
    });
  }, []);
  scheduleDeckReturnRef.current = scheduleDeckReturn;

  const leaveMatch = () => {
    openMatchFullscreen.current = false;
    const page = pageRef.current;
    const fullscreen = Boolean(page) && activeFullscreen() === page;
    if (!fullscreen) {
      exitBattleFullscreen(true);
      finishLeaveRef.current();
      return;
    }
    // Drop the battle field first. Exiting fullscreen and mounting the catalog in this
    // same turn is what freezes the window (the browser work after the React commit).
    leaveAfterFullscreen.current = true;
    setHoldBackdrop(true);
    setIsFullscreen(false);
  };

  const requestLeaveMatch = () => {
    if (state && state.phase !== 'game_over') {
      setLeaveConfirmOpen(true);
      return;
    }
    leaveMatch();
  };

  const onChallenge = async () => {
    if (!canChallenge || busy) return;
    setBusy(true);
    setRoomActionError(null);
    try {
      const id = await createChallenge(deck, {
        hostDeckPure: deckIsPure,
        pureOnlyBattle: !allowUnclaimedBadges,
      });
      navigate(battleRoomPath(id));
      try {
        await navigator.clipboard.writeText(`${window.location.origin}${battleRoomPath(id)}`);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      } catch {
        /* clipboard unavailable */
      }
    } catch (error) {
      console.error(error);
      setRoomActionError(ROOM_CREATE_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const onJoin = async () => {
    if (!canJoinWithDeck || busy) return;
    setBusy(true);
    setRoomActionError(null);
    try {
      await joinAsGuest(deck, deckIsPure);
    } catch (error) {
      console.error(error);
      setRoomActionError(ROOM_JOIN_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      console.error(error);
    }
  };

  const dismissFx = useCallback((key: string) => {
    setFxItems((current) => current.filter((fx) => fx.key !== key));
  }, []);

  useEffect(
    () => () => {
      fxTimers.current.forEach((id) => window.clearTimeout(id));
    },
    []
  );

  useEffect(() => {
    if (state?.phase === 'game_over') return;
    setHideResult(false);
  }, [state?.phase]);

  useEffect(() => {
    if (!liveState || liveState.phase !== 'game_over') return;
    if (recordedGameRef.current === liveState) return;
    recordedGameRef.current = liveState;
    preset?.onGameOver?.(liveState);
    setGameOverEpoch((n) => n + 1);
    if (preset || roomId || liveState.cpu !== 1 || liveState.autoplay || liveState.winner !== 0) return;
    recordCpuDifficultyWin(matchDifficultyRef.current);
    if (!profileId) return;
    recordBattleWin({
      profileId,
      name: playerName,
      gameId: `${matchSeedRef.current}-${Date.now()}`,
      kind: 'classic_win',
      difficulty: matchDifficultyRef.current,
      claimedOnly: Boolean(liveState.players[0].isPure) || (!BATTLE_ALLOW_UNCLAIMED && matchesSampleBattleDeck(deck)),
    });
  }, [deck, liveState, playerName, preset, profileId, roomId]);

  const showFx = useCallback(
    (items: BattleFxItem[]) => {
      if (!items.length) return;
      setFxItems((current) => mergeShownFx(current, items));
      items.forEach((item) => {
        fxTimers.current.push(window.setTimeout(() => dismissFx(item.key), fxDoneMs(item)));
      });
    },
    [dismissFx]
  );

  const stepQueue = useRef<PresentStep[]>([]);
  const stepTimer = useRef(0);
  const queuedTail = useRef<BattleState | null>(null);

  const runNextStep = useCallback(() => {
    const step = stepQueue.current.shift();
    if (!step) {
      stepTimer.current = 0;
      setPresenting(false);
      return;
    }
    setShownState(step.state);
    setTargetFx(step.targets);
    showFx(step.fx);
    stepTimer.current = window.setTimeout(runNextStep, step.holdMs);
  }, [showFx]);

  useEffect(() => () => window.clearTimeout(stepTimer.current), []);

  // Every new battle state joins one queue, so moves never play over each other.
  // Your own moves show at once. Same-pile Revealed with Effect is one longer pile label.
  useEffect(() => {
    const previous = queuedTail.current;
    if (liveState === previous) return;
    queuedTail.current = liveState;
    if (!liveState) {
      window.clearTimeout(stepTimer.current);
      stepTimer.current = 0;
      stepQueue.current = [];
      setShownState(null);
      setTargetFx([]);
      setPresenting(false);
      return;
    }
    const own = previous != null && ownInputFrom.current === previous;
    if (own) ownInputFrom.current = null;
    const fresh = previous == null || !liveState.fx.length;
    const steps = presentSteps(liveState, previous, viewing, fxSeq, own || fresh);
    // Your move right after an opponent's last step skips that step's closing pause.
    if (own && !stepQueue.current.length && stepTimer.current) {
      window.clearTimeout(stepTimer.current);
      stepTimer.current = 0;
    }
    stepQueue.current.push(...steps);
    if (!stepTimer.current) {
      setPresenting(true);
      runNextStep();
    }
  }, [liveState, runNextStep, viewing]);

  const concedeMatch = () => {
    setLeaveConfirmOpen(false);
    setHideResult(false);
    window.clearTimeout(stepTimer.current);
    stepTimer.current = 0;
    stepQueue.current = [];
    queuedTail.current = null;
    ownInputFrom.current = null;
    setShownState(null);
    setTargetFx([]);
    setPresenting(false);
    setCpuState((current) => {
      if (!current || current.phase === 'game_over') return current;
      return forfeitBattle(current, viewing);
    });
  };

  const replaceDeck = (next: string[]) => {
    setCatalogSectionFreeze([]);
    setDeck((current) =>
      next.length === current.length && next.every((id, index) => id === current[index]) ? current : next
    );
  };

  const toggleDeck = (id: string) => {
    if (editsLocked || lockSampleEdits) return;
    setDeck((current) => {
      let next = current;
      if (current.includes(id)) {
        next = current.filter((item) => item !== id);
        if (!next.length) setCatalogSectionFreeze([]);
      } else if (!ownedSet.has(id) || current.length >= BATTLE_DECK_SIZE) return current;
      else if (deckLimitBlocking(current.map(badgeRarity), badgeRarity(id))) return current;
      else next = [...current, id];
      setRandomizedIds([]);
      stopRandomFillFlash();
      if (usingSample) {
        customDeckRef.current = next;
        customRandomizedRef.current = [];
        setDeckSource(CUSTOM_BATTLE_DECK);
      }
      return next;
    });
  };

  const openBadgeModal = (badge: Badge) => {
    if (!onOpenBadge) return;
    if (activeFullscreen()?.classList.contains('bb-page')) exitBattleFullscreen(true);
    onOpenBadge(badge);
  };

  const deckSet = useMemo(() => new Set(deck), [deck]);
  const deckRarities = useMemo(() => deck.map(badgeRarity), [deck]);
  // Stable handlers so memoized catalog tiles skip re-rendering when unrelated state changes.
  const catalogClickRef = useRef<(badge: Badge, sectionId: BattleCatalogSectionId) => void>(() => undefined);
  catalogClickRef.current = (badge, sectionId) => {
    const owned = ownedSet.has(badge.id);
    const alreadyInDeck = deckSet.has(badge.id);
    const deckFull = deck.length >= BATTLE_DECK_SIZE || Boolean(deckLimitBlocking(deckRarities, badgeRarity(badge.id)));
    if (!editsLocked && !lockSampleEdits && (alreadyInDeck || (owned && !deckFull && !needsMoreBadges))) {
      pinCatalogScroll();
      setCatalogSectionFreeze(battleCatalogSectionFreeze(catalogSectionOrderRef.current, sectionId));
      toggleDeck(badge.id);
    } else {
      openBadgeModal(badge);
    }
  };
  const catalogSectionClicks = useMemo(() => {
    const clicks = {} as Record<BattleCatalogSectionId, (badge: Badge) => void>;
    for (const section of BATTLE_CATALOG_SECTIONS) {
      clicks[section.id] = (badge) => catalogClickRef.current(badge, section.id);
    }
    return clicks;
  }, []);
  const renderCatalogBadge = (badge: Badge, sectionId: BattleCatalogSectionId) => {
    const owned = ownedSet.has(badge.id);
    const alreadyInDeck = deckSet.has(badge.id);
    const limitHit = alreadyInDeck ? null : deckLimitBlocking(deckRarities, badgeRarity(badge.id));
    const deckFull = deck.length >= BATTLE_DECK_SIZE || Boolean(limitHit);
    const canToggle = !editsLocked && (alreadyInDeck || (owned && !deckFull && !needsMoreBadges));
    const openInstead = !owned || (deckFull && !alreadyInDeck);
    return (
      <BattleBadge
        key={`${sectionId}:${badge.id}`}
        badge={badge}
        catalogBadge={`${sectionId}:${badge.id}`}
        compact
        pill={claimPill(badge.id)}
        veil={
          alreadyInDeck
            ? 'Already in deck'
            : !owned
              ? 'Unclaimed'
              : limitHit && DECK_LIMITS[limitHit] < BATTLE_DECK_SIZE
                ? `Max ${DECK_LIMITS[limitHit]} ${limitHit}+`
                : null
        }
        onClick={canToggle || (openInstead && onOpenBadge) ? catalogSectionClicks[sectionId] : undefined}
      />
    );
  };
  const openBadgeModalRef = useRef(openBadgeModal);
  openBadgeModalRef.current = openBadgeModal;

  const deckBadges = useMemo(() => [...deckCards].sort(compareBattleDeck), [deckCards]);
  const deckStatTotals = useMemo(() => sumDeckStats(deckCards), [deckCards]);
  const curveNote = deckCurveNote(deckCards);

  const randomizePure = requirePure;
  const randomizePool = useMemo(
    () => (randomizePure ? playableBadges.filter((badge) => claimedSet.has(badge.id)) : playableBadges),
    [claimedSet, playableBadges, randomizePure]
  );
  const canRandomize = !waitingForGuest && !rollingRandom && randomizePool.length >= BATTLE_DECK_SIZE;
  const randomizedSet = useMemo(() => new Set(randomizedIds), [randomizedIds]);
  const selectedDeckIds = useMemo(
    () => (usingSample ? [] : deck.filter((id) => !randomizedSet.has(id))),
    [deck, randomizedSet, usingSample]
  );
  const selectDeckSource = (source: string) => {
    if (waitingForGuest || source === deckSource) return;
    didSeedDeck.current = true;
    if (source === CUSTOM_BATTLE_DECK) {
      setDeckSource(CUSTOM_BATTLE_DECK);
      replaceDeck(customDeckRef.current);
      setRandomizedIds(customRandomizedRef.current);
      return;
    }
    const next = sampleBattleDeckById(source);
    if (!next) return;
    if (deckSource === CUSTOM_BATTLE_DECK) {
      customDeckRef.current = deck;
      customRandomizedRef.current = randomizedIds;
    }
    setDeckSource(next.id);
    replaceDeck([...next.badgeIds]);
  };

  const needsMoreBadges = playableIds.length < BATTLE_DECK_SIZE;
  const showSamplesPrompt = deckSource === CUSTOM_BATTLE_DECK && deck.length === 0;
  const ownedDeckCount = deck.filter((id) => ownedSet.has(id)).length;
  const missingDeckCount = deck.length - ownedDeckCount;
  const isPureQueuedDeck = (player: PlayerId, battle?: BattleState | null) => {
    if (battle?.cpu === player) return false;
    if (battle) return Boolean(battle.players[player].isPure);
    if (room?.host) {
      const seated = player === 0 ? room.host : room.guest;
      return Boolean(seated?.isPure);
    }
    return player === 0 && deck.length > 0 && deck.every((id) => claimedSet.has(id));
  };
  const sampleExact = matchesSampleBattleDeck(deck);
  const customReady =
    !needsMoreBadges &&
    ownedDeckCount === BATTLE_DECK_SIZE &&
    missingDeckCount === 0 &&
    !deckLimitExceeded(deckRarities);
  const canStart = !rollingRandom && (customReady || sampleExact);
  const deckIsPure = deck.length > 0 && deck.every((id) => claimedSet.has(id));
  const creatingPureLobby = !allowUnclaimedBadges;
  const allowedInPureLobby = deckIsPure || sampleExact;
  const canChallenge = canStart && (!creatingPureLobby || allowedInPureLobby);
  const canJoinWithDeck = canStart && (!room?.pureOnlyBattle || allowedInPureLobby);
  const deckKey = `${cpuDifficulty}\0${deck.join('\0')}`;
  const knownPower = knownSampleDeckPower(deck, cpuDifficulty);
  const shownDeckPower = deckPower?.key === deckKey ? deckPower.percent : knownPower;
  const deckRatingTicket = useRef(0);
  const runDeckPower = useCallback(
    (ids: readonly string[]) => {
      if (preset || cpuDifficulty === 'impossible' || ids.length !== BATTLE_DECK_SIZE) return;
      const key = `${cpuDifficulty}\0${ids.join('\0')}`;
      const saved = knownSampleDeckPower(ids, cpuDifficulty);
      if (saved != null) {
        deckRatingTicket.current += 1;
        setRatingDeck(false);
        setDeckPower({ key, percent: saved });
        return;
      }
      const ticket = ++deckRatingTicket.current;
      setRatingDeck(true);
      window.setTimeout(() => {
        if (deckRatingTicket.current !== ticket) return;
        void rateDeckPowerAsync(
          ids,
          deckPowerGames(cpuDifficulty),
          cpuDifficulty,
          () => deckRatingTicket.current !== ticket
        )
          .then((percent) => {
            if (percent == null || deckRatingTicket.current !== ticket) return;
            setDeckPower({ key, percent });
          })
          .finally(() => {
            if (deckRatingTicket.current === ticket) setRatingDeck(false);
          });
      }, 0);
    },
    [cpuDifficulty, preset]
  );
  useEffect(() => {
    if (cpuDifficulty !== 'impossible') return;
    deckRatingTicket.current += 1;
    setRatingDeck(false);
  }, [cpuDifficulty]);
  useEffect(() => {
    if (cpuDifficulty === 'impossible' || preset || rollingRandom || deck.length !== BATTLE_DECK_SIZE) return;
    if (deckPower?.key === deckKey) return;
    runDeckPower(deck);
  }, [cpuDifficulty, deck, deckKey, deckPower, preset, rollingRandom, runDeckPower]);
  /**
   * Shows rolled decks one by one until `rate` clears `minPercent`. A null rating rejects the deck outright.
   * Always rolls at least `minRolls` and keeps the best. With `maxRolls`, settles for the best-rated deck once the rolls run out.
   */
  const rollDeckUntil = (
    roll: (seed: number) => string[],
    rate: (ids: string[], seed: number, isCancelled: () => boolean) => number | null | Promise<number | null>,
    minPercent: number,
    source: BattleDeckSource,
    {
      minRolls = 1,
      maxRolls = Infinity,
      keep = [],
    }: { minRolls?: number; maxRolls?: number; keep?: readonly string[] } = {}
  ) => {
    const ticket = ++deckRatingTicket.current;
    setRollingRandom(true);
    setRatingDeck(true);
    stopRandomFillFlash();
    let attempt = 0;
    let best: { ids: string[]; percent: number } | null = null;
    const base = Date.now() % 1_000_000 || 1;
    const keepSet = new Set(keep);
    const markFill = (ids: readonly string[]) => setRandomizedIds(ids.filter((id) => !keepSet.has(id)));
    const settle = (ids: string[], percent: number) => {
      replaceDeck(ids);
      markFill(ids);
      if (cpuDifficulty !== 'impossible') {
        // Scout % is only for picking a deck. Drop it so the full estimate can run after the search.
        setDeckPower((current) => (current?.key === `${cpuDifficulty}\0${ids.join('\0')}` ? current : null));
      }
      setCatalogSectionFreeze([]);
      setRatingDeck(false);
      setRollingRandom(false);
      startRandomFillFlash();
    };
    const showNext = () => {
      if (deckRatingTicket.current !== ticket) {
        setRollingRandom(false);
        return;
      }
      const seed = base + attempt * 7919;
      attempt += 1;
      const next = roll(seed);
      didSeedDeck.current = true;
      setDeckSource(source);
      setDeck(next);
      markFill(next);
      window.setTimeout(() => {
        void scoreShown(next, seed);
      }, 0);
    };
    const scoreShown = async (ids: string[], seed: number) => {
      if (deckRatingTicket.current !== ticket) {
        setRollingRandom(false);
        return;
      }
      let percent: number | null = null;
      try {
        percent = await Promise.resolve(rate(ids, seed, () => deckRatingTicket.current !== ticket));
      } catch {
        percent = null;
      }
      if (deckRatingTicket.current !== ticket) {
        setRollingRandom(false);
        return;
      }
      if (percent != null && (!best || percent > best.percent)) best = { ids, percent };
      if (best && attempt >= minRolls && (best.percent >= minPercent || attempt >= maxRolls)) {
        const final = best;
        if (final.ids !== ids) {
          // Let the last roll register before jumping back to an earlier, better deck.
          window.setTimeout(() => {
            if (deckRatingTicket.current !== ticket) {
              setRollingRandom(false);
              return;
            }
            settle(final.ids, final.percent);
          }, 200);
          return;
        }
        settle(best.ids, best.percent);
        return;
      }
      if (attempt >= maxRolls) {
        settle(best?.ids ?? ids, best?.percent ?? 0);
        return;
      }
      window.setTimeout(showNext, 0);
    };
    window.setTimeout(showNext, 0);
  };
  const canSaveDeck = canStart;

  const fillRandomDeck = (keep: readonly string[]) => {
    if (!canRandomize) return;
    // Impossible never simulates; reuse Hard's search so Randomize still lands a viable deck.
    const rateVs = cpuDifficulty === 'impossible' ? 'hard' : cpuDifficulty;
    rollDeckUntil(
      (seed) => recommendBattleDeck(randomizePool, BATTLE_DECK_SIZE, seed, keep),
      (ids, _seed, isCancelled) => rateDeckPowerAsync(ids, recommendedDeckGames(rateVs), rateVs, isCancelled),
      recommendedDeckMinWinRate(rateVs),
      CUSTOM_BATTLE_DECK,
      { minRolls: RECOMMENDED_DECK_MIN_ROLLS, maxRolls: RECOMMENDED_DECK_MAX_ROLLS, keep }
    );
  };

  const applyRecommendedDeck = () => {
    if (!canRandomize) return;
    const keep = usingSample || selectedDeckIds.length >= BATTLE_DECK_SIZE ? [] : selectedDeckIds;
    fillRandomDeck(keep);
  };

  const openSaveDeck = () => {
    if (!canSaveDeck) return;
    const matching = savedDecks.find((saved) => deckIdsMatch(saved.badgeIds, deck));
    setSaveName(matching?.name ?? sampleDeck?.name ?? '');
    setSaveOpen(true);
  };

  const closeSaveDeck = () => {
    setSaveOpen(false);
  };

  const submitSaveDeck = (event: React.FormEvent) => {
    event.preventDefault();
    const saved = saveBattleDeck(saveName, deck);
    if (!saved) return;
    setSavedDecks(listSavedBattleDecks());
    closeSaveDeck();
  };

  const loadSavedDeck = (saved: SavedBattleDeck) => {
    if (waitingForGuest) return;
    didSeedDeck.current = true;
    const ids = saved.badgeIds.filter((id) => isBattleBadge(id)).slice(0, BATTLE_DECK_SIZE);
    customDeckRef.current = ids;
    customRandomizedRef.current = [];
    setRandomizedIds([]);
    stopRandomFillFlash();
    setDeckSource(CUSTOM_BATTLE_DECK);
    replaceDeck(ids);
  };

  const removeSavedDeck = (id: string) => {
    setSavedDecks(deleteSavedBattleDeck(id));
  };

  const exportDeckCode = async (key: string, badgeIds: readonly string[]) => {
    if (!badgeIds.length) return;
    const code = formatBattleDeckCode(badgeIds);
    try {
      await navigator.clipboard.writeText(code);
      setCopiedDeckCode(key);
      window.setTimeout(() => setCopiedDeckCode((current) => (current === key ? null : current)), 1600);
    } catch {
      /* clipboard unavailable */
    }
  };

  const wipeDeck = () => {
    didSeedDeck.current = true;
    customDeckRef.current = [];
    customRandomizedRef.current = [];
    setRandomizedIds([]);
    stopRandomFillFlash();
    setDeckSource(CUSTOM_BATTLE_DECK);
    replaceDeck([]);
  };

  const clearDeck = () => {
    if (waitingForGuest || !deck.length) return;
    wipeDeck();
  };

  const importDeckCode = async () => {
    if (editsLocked) return;
    try {
      const ids = parseBattleDeckCode(await navigator.clipboard.readText());
      if (!ids.length) return;
      didSeedDeck.current = true;
      setRandomizedIds([]);
      stopRandomFillFlash();
      customRandomizedRef.current = [];
      if (usingSample) {
        customDeckRef.current = ids;
        setDeckSource(CUSTOM_BATTLE_DECK);
      }
      replaceDeck(ids);
    } catch {
      /* clipboard unavailable or denied */
    }
  };

  const randomizeTitle =
    randomizePure && randomizePool.length < BATTLE_DECK_SIZE
      ? `Claim at least ${BATTLE_DECK_SIZE} battle badges to randomize from them`
      : undefined;
  const claimedOnlyLocked = Boolean(roomId || preset);
  const showClaimedOnlyChrome = BATTLE_ALLOW_UNCLAIMED;
  const claimedOnlySwitch = !showClaimedOnlyChrome ? null : claimedOnlyLocked ? (
    <span className={`bb-claimed-switch${requirePure ? ' is-on' : ''} is-static`}>
      <span className="bb-claimed-switch-label">{BATTLE_PURE_MODE_LABELS.pure}</span>
    </span>
  ) : (
    <HoverTip text="Only badges you've claimed on your profile can go in your deck.">
      <button
        type="button"
        className={`bb-claimed-switch${claimedOnly ? ' is-on' : ''}`}
        aria-pressed={claimedOnly}
        onClick={() => {
          const next = !claimedOnly;
          setClaimedOnly(next);
          writeBattleClaimedOnly(next);
        }}
      >
        <span className="bb-claimed-switch-label">{BATTLE_PURE_MODE_LABELS.pure}</span>
        <span className="bb-claimed-switch-track" aria-hidden>
          <span className="bb-claimed-switch-knob" />
        </span>
      </button>
    </HoverTip>
  );
  const randomizeButton = (
    <HoverTip text={randomizeTitle}>
      <button
        type="button"
        className={`bb-recommend${showClaimedOnlyChrome && requirePure ? ' is-pure' : ''}`}
        disabled={!canRandomize}
        onClick={applyRecommendedDeck}
      >
        Randomize Deck
      </button>
    </HoverTip>
  );
  const chooseDeckHint = !canStart
    ? needsMoreBadges
      ? 'Pick a sample deck, or claim more battle badges'
      : 'Choose 12 badges first'
    : null;

  const saveDeckButton = (
    <button type="button" className="bb-recommend" disabled={!canSaveDeck} onClick={() => openSaveDeck()}>
      Save deck
    </button>
  );

  if (room?.abandonedBy) {
    return (
      <div className="bb-page">
        <BattlePageHeader
          subtitle={`${room.abandonedBy.name} left the battle.`}
          actions={
            <button type="button" className="bb-start" onClick={leaveMatch}>
              Leave battle
            </button>
          }
        />
      </div>
    );
  }

  if (roomId && isFull) {
    return (
      <div className="bb-page">
        <BattlePageHeader
          subtitle="This battle already has two players."
          actions={
            <button type="button" className="bb-start" onClick={() => navigate(BATTLE_BADGES_PATH)}>
              Back to Battle
            </button>
          }
        />
      </div>
    );
  }

  if (invalidRoom || (roomId && status === 'missing')) {
    return (
      <div className="bb-page">
        <BattlePageHeader
          subtitle="This battle link is missing or expired."
          actions={
            <button type="button" className="bb-start" onClick={() => navigate(BATTLE_BADGES_PATH)}>
              Back to Battle
            </button>
          }
        />
      </div>
    );
  }

  if (roomId && status !== 'ready') {
    return (
      <div className="bb-page">
        <BattlePageHeader
          subtitle="This battle is still being set up. Hang on a moment."
          actions={
            <button type="button" className="bb-start" onClick={() => navigate(BATTLE_BADGES_PATH)}>
              Back to Battle
            </button>
          }
        />
      </div>
    );
  }

  if (!state) {
    if (preset) return <div className="bb-page" ref={bindBattlePage} />;
    return (
      <div className="bb-page is-setup" ref={bindBattlePage}>
        <BattlePageHeader
          subtitle={roomActionError ?? (showClaimedOnlyChrome && room?.pureOnlyBattle ? PURE_LOBBY_HINT : undefined)}
          subtitleClassName="bb-sub is-pure"
          extra={waitingForGuest && shareUrl ? <p className="bb-share-url">{shareUrl}</p> : null}
          actions={
            waitingForGuest ? (
              <>
                <button type="button" className="bb-recommend" onClick={() => void copyLink()}>
                  {copied ? 'Copied' : 'Copy link'}
                </button>
                <button type="button" className="bb-resign" onClick={leaveMatch}>
                  Cancel
                </button>
              </>
            ) : canJoin ? (
              <>
                {claimedOnlySwitch}
                {randomizeButton}
                <HoverTip text={chooseDeckHint ?? (!canJoinWithDeck ? PURE_DECK_REQUIRED_HINT : null)}>
                  <button
                    type="button"
                    className="bb-start"
                    disabled={!canJoinWithDeck || busy}
                    onClick={() => void onJoin()}
                  >
                    Ready
                  </button>
                </HoverTip>
              </>
            ) : (
              <>
                {claimedOnlySwitch}
                {randomizeButton}
                <HoverTip text={chooseDeckHint ?? (!canChallenge ? PURE_DECK_REQUIRED_HINT : null)}>
                  <button
                    type="button"
                    className={`bb-recommend${showClaimedOnlyChrome && requirePure ? ' is-pure' : ''}`}
                    disabled={!canChallenge || busy}
                    onClick={() => void onChallenge()}
                  >
                    Challenge a person
                  </button>
                </HoverTip>
                <div className={`bb-start-split is-${cpuDifficulty}`} ref={difficultyMenuRef}>
                  <span className="bb-start-main">
                    <HoverTip text={chooseDeckHint}>
                      <button type="button" className="bb-start" disabled={!canStart} onClick={() => startMatch()}>
                        Battle vs CPU
                      </button>
                    </HoverTip>
                  </span>
                  <button
                    type="button"
                    className="bb-start bb-start-arrow"
                    aria-label="Choose CPU difficulty"
                    aria-haspopup="menu"
                    aria-expanded={difficultyMenuOpen}
                    onClick={() => setDifficultyMenuOpen((open) => !open)}
                  >
                    ▾
                  </button>
                  {difficultyMenuOpen ? (
                    <div className="bb-difficulty-menu" role="menu">
                      {CPU_DIFFICULTIES.map((level) => {
                        const locked = !isCpuDifficultyUnlocked(level);
                        const hint = cpuDifficultyUnlockHint(level);
                        const hintId = `bb-difficulty-tip-${level}`;
                        return (
                          <div
                            key={level}
                            className={`bb-difficulty-item-wrap${locked ? ' is-locked' : ''}`}
                            aria-describedby={hint ? hintId : undefined}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              disabled={locked}
                              className={`bb-difficulty-item is-${level}${level === cpuDifficulty ? ' is-current' : ''}${locked ? ' is-locked' : ''}`}
                              onClick={() => {
                                if (locked) return;
                                setDifficultyMenuOpen(false);
                                setCpuDifficulty(level);
                                writeCpuDifficulty(level);
                              }}
                            >
                              {CPU_DIFFICULTY_LABELS[level]}
                            </button>
                            {hint ? (
                              <span className="bb-deck-pure-tip" id={hintId} role="tooltip">
                                {hint}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </>
            )
          }
        />
        <section className="bb-setup">
          <div className="bb-deck-row">
            <section className="gallery-group">
              <DeckGroupFill totals={deckStatTotals} count={deck.length} />
              <div className="gallery-group-content">
                <header className="gallery-group-head">
                  <DeckGroupSwatch totals={deckStatTotals} count={deck.length} />
                  <div className="gallery-group-titles">
                    <div className="bb-deck-title-row">
                      <h2 className="gallery-group-name">{deckTitle}</h2>
                      {showClaimedOnlyChrome && deckIsPure ? <ClaimedOnlyPill hintId="bb-deck-pure-tip-yours" /> : null}
                    </div>
                    <p className="gallery-group-meta">
                      {deck.length === BATTLE_DECK_SIZE ? (
                        <DeckPowerMeta
                          percent={shownDeckPower}
                          pending={ratingDeck}
                          difficulty={cpuDifficulty}
                          disabled={rollingRandom}
                          onRate={() => runDeckPower(deck)}
                        />
                      ) : (
                        <span className={canStart ? 'is-ready' : undefined}>
                          {`${ownedDeckCount}/${BATTLE_DECK_SIZE}`}
                        </span>
                      )}
                    </p>
                  </div>
                  <GalleryGroupRarityCounts badgeIds={deck} badgesMap={badgesMap} />
                  <div className="bb-group-actions">
                    <label className={`bb-deck-source${showSamplesPrompt ? ' is-samples' : ''}`}>
                      <select
                        aria-label="Deck"
                        value={showSamplesPrompt ? '' : deckSource}
                        disabled={waitingForGuest || rollingRandom}
                        onChange={(event) => {
                          selectDeckSource(event.target.value);
                          event.currentTarget.blur();
                        }}
                      >
                        <option disabled value="divider">
                          ────────
                        </option>
                        {showSamplesPrompt ? (
                          <option value="" disabled hidden>
                            Samples
                          </option>
                        ) : (
                          <>
                            <option value={CUSTOM_BATTLE_DECK}>
                              {usingSample ? 'Switch to Custom Deck' : 'Custom Deck'}
                            </option>
                            <option disabled value="divider">
                              ────────
                            </option>
                          </>
                        )}
                        <option value={DECK_OF_THE_DAY}>Deck of the Day</option>
                        {SAMPLE_BATTLE_DECKS.map((sample) => (
                          <option key={sample.id} value={sample.id}>
                            {sample.name}
                          </option>
                        ))}
                        <option disabled value="divider">
                          ────────
                        </option>
                      </select>
                    </label>
                    <button
                      type="button"
                      className="bb-recommend"
                      disabled={waitingForGuest || rollingRandom || !deck.length}
                      onClick={clearDeck}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="bb-recommend"
                      disabled={editsLocked}
                      onClick={() => void importDeckCode()}
                    >
                      Import
                    </button>
                    {saveDeckButton}
                  </div>
                  <DeckStats totals={deckStatTotals} borderless />
                </header>
                {requirePure && missingDeckCount > 0 && !sampleExact ? (
                  <p className="bb-curve-note is-pure">
                    {missingDeckCount === 1
                      ? 'You must remove the unclaimed badge from your deck to play.'
                      : `You must remove the ${missingDeckCount} unclaimed badges from your deck to play.`}
                  </p>
                ) : deck.length === BATTLE_DECK_SIZE && curveNote ? (
                  <p className="bb-curve-note">{curveNote}</p>
                ) : usingSample ? (
                  <p className="bb-curve-note is-sample">{sampleDeck.blurb}</p>
                ) : null}
                <div className="gallery-badge-grid">
                  {Array.from({ length: BATTLE_DECK_SIZE }, (_, index) => {
                    const badge = deckBadges[index];
                    const missing = badge ? !ownedSet.has(badge.id) : false;
                    const pill = badge ? claimPill(badge.id) : undefined;
                    return badge ? (
                      <BattleBadge
                        key={badge.id}
                        badge={badge}
                        grayscale={rollingRandom || (missing && !sampleExact)}
                        compact
                        randomFill={randomFillFlash && !rollingRandom && randomizedSet.has(badge.id)}
                        pill={pill}
                        hideEffects={missing && requirePure && !sampleExact}
                        playHint={
                          missing && !lockSampleEdits
                            ? 'You have not claimed this badge. Click to remove it.'
                            : undefined
                        }
                        onClick={
                          rollingRandom
                            ? undefined
                            : editsLocked || lockSampleEdits
                              ? () => openBadgeModal(badge)
                              : () => toggleDeck(badge.id)
                        }
                      />
                    ) : (
                      <div key={`empty-${index}`} className="gallery-badge bb-slot">
                        <span>{index + 1}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </div>
          {savedDecks.length ? (
            <div className="bb-saved-decks">
              {savedDecks.map((saved) => {
                const loaded = deckSource === CUSTOM_BATTLE_DECK && deckIdsMatch(saved.badgeIds, deck);
                const savedBadges = saved.badgeIds
                  .map((id) => badgesMap.get(id))
                  .filter((badge): badge is Badge => Boolean(badge))
                  .sort(compareBattleDeck);
                const savedTotals = sumDeckStats(savedBadges);
                return (
                  <section key={saved.id} className={`gallery-group bb-saved-deck${loaded ? ' is-loaded' : ''}`}>
                    <DeckGroupFill
                      totals={savedTotals}
                      count={saved.badgeIds.length}
                      opacity={loaded ? SAVED_DECK_WASH_OPACITY_LOADED : SAVED_DECK_WASH_OPACITY}
                    />
                    <div className="gallery-group-content">
                      <header className="gallery-group-head">
                        <DeckGroupSwatch totals={savedTotals} count={saved.badgeIds.length} />
                        <div className="gallery-group-titles">
                          <div className="bb-deck-title-row">
                            <h2 className="gallery-group-name">{saved.name}</h2>
                            {showClaimedOnlyChrome &&
                            saved.badgeIds.length > 0 &&
                            saved.badgeIds.every((id) => claimedSet.has(id)) ? (
                              <ClaimedOnlyPill hintId={`bb-deck-pure-tip-${saved.id}`} />
                            ) : null}
                          </div>
                          <p className={`gallery-group-meta${loaded ? ' is-ready' : ''}`}>
                            {loaded ? 'Loaded' : `${saved.badgeIds.length}/${BATTLE_DECK_SIZE}`}
                          </p>
                        </div>
                        <GalleryGroupRarityCounts badgeIds={saved.badgeIds} badgesMap={badgesMap} />
                        <div className="bb-group-actions">
                          <button
                            type="button"
                            className="bb-recommend"
                            disabled={waitingForGuest || rollingRandom}
                            onClick={() => loadSavedDeck(saved)}
                          >
                            Load
                          </button>
                          <button
                            type="button"
                            className="bb-recommend"
                            disabled={rollingRandom}
                            onClick={() => void exportDeckCode(saved.id, saved.badgeIds)}
                          >
                            {copiedDeckCode === saved.id ? 'Copied' : 'Export'}
                          </button>
                          <button
                            type="button"
                            className="bb-resign"
                            disabled={rollingRandom}
                            onClick={() => setDeckToDelete(saved)}
                          >
                            Delete
                          </button>
                        </div>
                        <DeckStats totals={savedTotals} borderless />
                      </header>
                    </div>
                  </section>
                );
              })}
            </div>
          ) : null}
          <section className="gallery-group gallery-group--freeform bb-more">
            <div className="gallery-group-content">
              <header className="gallery-group-head">
                <span className="gallery-group-swatch gallery-group-swatch--muted" aria-hidden />
                <div className="gallery-group-titles">
                  <h2 className="gallery-group-name">
                    {canDraftCatalog ? battleCatalogTitle(rarityFilter) : 'Battle Badges'}
                  </h2>
                  <p className="gallery-group-meta">
                    {canDraftCatalog
                      ? `${catalog.owned.length} of ${catalog.owned.length + catalog.unclaimed.length} owned`
                      : `${battleCatalogTally.owned} of ${battleCatalogTally.total} owned`}
                  </p>
                </div>
                {canDraftCatalog ? (
                  <div className="bb-catalog-filters">
                    <label className={`bb-deck-source${rarityFilter !== 'all' ? ` is-active is-${rarityFilter}` : ''}`}>
                      <select
                        aria-label="Filter by rarity"
                        value={rarityFilter}
                        onChange={(event) => {
                          const value = event.target.value;
                          setRarityFilter(CATALOG_RARITY_FILTERS.find((rarity) => rarity === value) ?? 'all');
                          event.currentTarget.blur();
                        }}
                      >
                        <option value="all">All rarities</option>
                        {CATALOG_RARITY_FILTERS.map((rarity) => (
                          <option key={rarity} value={rarity}>
                            {RARITY_TIER_LABELS[rarity]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className={`bb-catalog-search${catalogSearch ? ' has-clear' : ''}`}>
                      <label className="bb-catalog-search-label">
                        <span className="bb-catalog-search-icon" aria-hidden>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                          >
                            <circle cx="11" cy="11" r="7" />
                            <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
                          </svg>
                        </span>
                        <input
                          ref={catalogSearchRef}
                          type="search"
                          value={catalogSearch}
                          placeholder="Search badges…"
                          autoComplete="off"
                          aria-label="Search battle badges"
                          onChange={(event) => setCatalogSearch(event.target.value)}
                        />
                        {catalogSearch ? (
                          <button
                            type="button"
                            className="bb-catalog-search-clear"
                            aria-label="Clear search"
                            onClick={() => {
                              setCatalogSearch('');
                              catalogSearchRef.current?.focus();
                            }}
                          >
                            Clear
                          </button>
                        ) : null}
                      </label>
                    </div>
                  </div>
                ) : null}
              </header>
              {canDraftCatalog ? (
                <>
                  <div className="bb-picker-deck-stats">
                    <DeckStats totals={deckStatTotals} />
                  </div>
                  {catalog.items.length ? (
                    <div ref={catalogBucketsRef} className="bb-catalog-buckets">
                      {displayBuckets.map((section, index) => {
                        const isAll = section.id === 'all';
                        const claimedOpen = openCatalogSection === section.id;
                        const unclaimedOpen = openCatalogUnclaimed === section.id;
                        const open = claimedOpen || unclaimedOpen;
                        const previewCap = catalogSectionPreview(section.id);
                        const { owned, unclaimed } = catalogSectionOwnedInOrder(
                          section.badges,
                          claimedSet,
                          catalogSortDeckIds
                        );
                        const claimedOverflow = catalogSectionClaimedOverflow(
                          owned.length,
                          unclaimed.length,
                          previewCap
                        );
                        const claimedPreview = claimedOverflow ? owned.slice(0, previewCap) : owned;
                        const claimedHidden = claimedOverflow ? owned.slice(previewCap) : [];
                        const showUnclaimedMore = unclaimed.length > 0 && (!claimedOverflow || claimedOpen);
                        const prev = displayBuckets[index - 1];
                        const next = displayBuckets[index + 1];
                        const showBeforeAll = isAll && prev != null && isBattleCatalogStatSection(prev.id);
                        const afterRoles =
                          section.id === 'towers' ||
                          (!displayBuckets.some((bucket) => bucket.id === 'towers') &&
                            next != null &&
                            section.id !== 'all' &&
                            !isBattleCatalogStatSection(section.id) &&
                            (isBattleCatalogStatSection(next.id) || next.id === 'all'));
                        const unclaimedMore = (
                          <CatalogMoreTile
                            count={showUnclaimedMore ? unclaimed.length : 0}
                            expanded={unclaimedOpen}
                            hiddenLabel="unclaimed"
                            onToggle={() => toggleCatalogUnclaimed(section.id)}
                          />
                        );
                        return (
                          <React.Fragment key={section.id}>
                            {showBeforeAll ? (
                              <div className="bb-picker-deck-stats is-mid">
                                <DeckStats totals={deckStatTotals} borderless />
                              </div>
                            ) : null}
                            <section
                              data-bb-catalog={section.id}
                              aria-labelledby={`bb-catalog-title-${section.id}`}
                              className={`bb-catalog-bucket${open ? ' is-open' : ''}`}
                            >
                              <header className="bb-catalog-bucket-head">
                                <h3 id={`bb-catalog-title-${section.id}`} className="bb-catalog-bucket-title">
                                  {section.title}
                                </h3>
                              </header>
                              <div className="gallery-badge-grid">
                                {claimedPreview.map((badge) => renderCatalogBadge(badge, section.id))}
                                <CatalogMoreTile
                                  count={claimedOverflow}
                                  expanded={claimedOpen}
                                  hiddenLabel="badges"
                                  onToggle={() => toggleCatalogSection(section.id)}
                                />
                                {claimedOpen
                                  ? claimedHidden.map((badge) => renderCatalogBadge(badge, section.id))
                                  : null}
                                {unclaimedMore}
                                {unclaimedOpen ? unclaimed.map((badge) => renderCatalogBadge(badge, section.id)) : null}
                              </div>
                            </section>
                            {afterRoles ? (
                              <div className="bb-picker-deck-stats is-mid">
                                <DeckStats totals={deckStatTotals} borderless />
                              </div>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="bb-empty-hand">No battle badges match these filters.</p>
                  )}
                </>
              ) : (
                <p className="bb-catalog-need-more">
                  You don't have enough battle badges to draft a deck.
                  <span>Sample Decks are already packed. Pick one and play.</span>
                </p>
              )}
            </div>
          </section>
        </section>
        {saveOpen ? (
          <div className="bb-overlay" onClick={closeSaveDeck}>
            <form
              className="bb-modal"
              role="dialog"
              aria-label="Save deck"
              onClick={(event) => event.stopPropagation()}
              onSubmit={submitSaveDeck}
            >
              <h2>Save deck</h2>
              <input
                value={saveName}
                onChange={(event) => setSaveName(event.target.value)}
                placeholder="Name this deck"
                maxLength={48}
                autoFocus
                aria-label="Deck name"
              />
              <button type="submit" disabled={!saveName.trim()}>
                Save
              </button>
              <button type="button" className="is-ghost" onClick={closeSaveDeck}>
                Cancel
              </button>
            </form>
          </div>
        ) : null}
        {deckToDelete ? (
          <div className="bb-overlay" onClick={() => setDeckToDelete(null)}>
            <div
              className="bb-modal"
              role="dialog"
              aria-label="Delete deck"
              onClick={(event) => event.stopPropagation()}
            >
              <h2>Delete deck</h2>
              <p>Are you sure you want to delete {deckToDelete.name || 'this deck'}?</p>
              <button
                type="button"
                onClick={() => {
                  removeSavedDeck(deckToDelete.id);
                  setDeckToDelete(null);
                }}
              >
                Delete
              </button>
              <button type="button" className="is-ghost" onClick={() => setDeckToDelete(null)} autoFocus>
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const pending = state.pending;
  const coaching = preset?.coaching ? preset.coaching(state) : Boolean(preset?.allowInput);
  const allowed = (input: BattleInput) => !preset?.allowInput || preset.allowInput(state, input);
  const focusStat = preset?.coachStat?.(state) ?? null;
  const coachOverview = Boolean(preset?.coachOverview?.(state));
  const revealable = revealableCards(state)
    .map((item) => ({
      ...item,
      piles: item.piles.filter((pile) => allowed({ type: 'reveal', cardId: item.cardId, pile })),
    }))
    .filter((item) => item.piles.length > 0);
  const playables = playablePiles(state).filter((pile) => allowed({ type: 'play', pile }));
  const ignoreStatLead = statLeadIsIgnored(state);
  const blockedRevealRarities = new Set(PILE_MAX_RARITY.filter((rarity) => revealRarityIsBlocked(state, rarity)));

  const onHandCard = (player: PlayerId, cardId: string) => {
    if (
      pending?.player === player &&
      ((pending.type === 'choose_hand' && pending.cardIds.includes(cardId)) ||
        (pending.type === 'choose_pile' && pending.options?.includes(cardId)))
    ) {
      apply({ type: 'resolve', target: { kind: 'hand', cardId } });
      return;
    }
    if (player !== state.activePlayer || pending) return;
    const option = revealable.find((item) => item.cardId === cardId);
    if (!option) return;
    setHoverRevealPile(null);
    setRevealPick((current) => (current === cardId ? null : cardId));
  };

  const onPile = (owner: PlayerId, pile: PileIndex) => {
    // A valid pending target wins over undo, so the activating pile can itself be the target.
    if (pending?.type === 'choose_pile' && pending.player === owner && pending.piles.includes(pile)) {
      apply({ type: 'resolve', target: { kind: 'pile', pile } });
      return;
    }
    if (pending?.type === 'choose_board_badge' && pending.owner === owner && pending.piles.includes(pile)) {
      apply({ type: 'resolve', target: { kind: 'board_badge', owner, pile } });
      return;
    }
    if (state.undo && state.undo.player === owner && state.undo.pile === pile) {
      apply({ type: 'undo' });
      return;
    }
    if (revealPick && owner === state.activePlayer && !pending) {
      const option = revealable.find((item) => item.cardId === revealPick);
      if (option?.piles.includes(pile)) {
        apply({ type: 'reveal', cardId: revealPick, pile });
      }
      return;
    }
    if (owner === state.activePlayer && playables.includes(pile) && !pending && !revealPick) {
      apply({ type: 'play', pile });
    }
  };

  const foe = opponentOf(viewing);
  const resultTitle = state.winner === 'tie' ? 'Draw' : `${state.players[state.winner ?? 0].name} wins`;
  const boardStats: [ReturnType<typeof totalStats>, ReturnType<typeof totalStats>] = [
    totalStats(state, state.players[0]),
    totalStats(state, state.players[1]),
  ];
  const overlayFor = (player: PlayerId, zone: 'hand' | 'discard' | 'board' | 'deck'): BattleFxItem[] =>
    fxItems.filter((item) => {
      if (item.player !== player) return false;
      // Shown on the pile or the Prestige counter instead.
      if (PILE_LABEL_FX.has(item.kind) && item.kind !== 'play') return false;
      if (item.kind === 'stat' || item.kind === 'prestige') return false;
      if (item.kind === 'shuffle') return zone === 'deck';
      if (item.kind === 'draw' && item.player === viewing) return false;
      if (item.kind === 'play') {
        if (item.pile != null && pileCard(state.players[item.player], item.pile) === item.cardId) return false;
        if (
          fxItems.some(
            (other) =>
              other !== item && other.cardId === item.cardId && (other.kind === 'return' || other.kind === 'discard')
          )
        ) {
          return false;
        }
        return zone === 'board';
      }
      if (zone === 'hand') {
        return item.kind === 'draw' || item.kind === 'exchange' || (item.kind === 'return' && item.to !== 'deck');
      }
      if (zone === 'discard') return item.kind === 'discard';
      if (zone === 'deck') return item.kind === 'return' && item.to === 'deck';
      return false;
    });

  const renderActionsLeft = (player: PlayerId) => {
    if (state.phase === 'game_over') return null;
    const left = scheduledActionsLeft(state, player);
    if (!Number.isFinite(left)) return null;
    const lastChance = prestigeRaceIsClosing(state);
    const urgency = lastChance ? ' is-last-chance' : left <= 2 ? ' is-low' : left <= 4 ? ' is-warn' : '';
    return (
      <span className={`bb-actions-left${urgency}`}>
        {left} action{left === 1 ? '' : 's'} left
      </span>
    );
  };

  /** Either player wins the moment they hit the target, so flag it once they're close. */
  const renderInstantWinHint = (player: PlayerId) => {
    if (state.phase === 'game_over') return null;
    if (state.players[player].prestige < INSTANT_WIN_HINT_PRESTIGE) return null;
    return <span className="bb-instant-win">{prestigeToWin(state)} Prestige wins immediately</span>;
  };

  const renderPrestige = (player: PlayerId) => {
    const who = state.players[player];
    const mine = who.prestige;
    const theirs = state.players[opponentOf(player)].prestige;
    const lead = mine > theirs ? ' is-ahead' : mine < theirs ? ' is-behind' : '';
    const side = player === viewing ? ' is-mine' : ' is-foe';
    const shielded = prestigeIsShielded(state, player);
    const prestigeDelta = prestigeFxDelta(fxItems, player);
    return (
      <div
        className={`bb-prestige${lead}${side}${shielded ? ' is-shielded' : ''}${coachOverview ? ' is-coached' : ''}`}
        aria-label={`${who.name} prestige ${mine}${shielded ? ', cannot be stolen' : ''}`}
      >
        {shielded ? <PrestigeShieldLocks /> : null}
        <span className="bb-prestige-label">Prestige</span>
        <strong className="bb-prestige-val">{mine}</strong>
        {prestigeDelta ? (
          <span className={`bb-prestige-pop${prestigeDelta < 0 ? ' is-loss' : ''}`} aria-hidden>
            {prestigeDelta > 0 ? '+' : ''}
            {prestigeDelta}
          </span>
        ) : null}
      </div>
    );
  };

  const renderPiles = (player: PlayerId) => {
    const who = state.players[player];
    const isMine = player === viewing;
    const discarded = who.discard;
    const isActive = state.phase !== 'game_over' && state.activePlayer === player;
    const revealPiles =
      revealPick && isMine && state.activePlayer === viewing
        ? (revealable.find((item) => item.cardId === revealPick)?.piles ?? [])
        : pending?.type === 'choose_pile' && pending.player === player && isMine
          ? pending.piles
          : [];
    const targeting = pending?.type === 'choose_board_badge' && pending.owner === player;
    // Red when the pick costs you: your own badge you're giving up, or any of yours your opponent is choosing from.
    const losingBoardPick =
      pending?.type === 'choose_board_badge' &&
      pending.owner === viewing &&
      (pending.player !== viewing || LOSING_BOARD_PICKS.has(pending.purpose));
    const seat = state.openingPlayer === player ? 1 : 2;
    const finalCloser = seat === 2 && state.phase !== 'game_over' && prestigeRaceIsClosing(state);
    const previewCardId = isMine
      ? (revealPick ??
        (pending?.type === 'choose_pile' && pending.purpose === 'reveal' && pending.player === player
          ? pending.cardId
          : null))
      : null;
    const preview = previewCardId
      ? projectBoardStats(
          state,
          boardStats[player],
          previewCardId,
          hoverRevealPile != null && revealPiles.includes(hoverRevealPile) ? pileCard(who, hoverRevealPile) : null
        )
      : null;
    const pure = isPureQueuedDeck(player, state);
    return (
      <div
        className={`bb-lane${isActive ? ' is-active' : ''}${isMine ? ' is-mine' : ' is-foe'}${
          targeting ? ' is-targeting' : ''
        }${pure ? ' is-pure' : ''}`}
      >
        <span className="bb-lane-label">
          <HoverTip
            text={seat === 1 ? `${who.name} goes first each turn` : `${who.name} goes second and makes the last move`}
          >
            <span className={`bb-lane-seat${finalCloser ? ' is-final' : ''}`}>P{seat}</span>
          </HoverTip>
          {cpuDrives(state, player) && seatDifficulty(state, player) !== 'easy' ? (
            <span>
              <span className={`bb-cpu-difficulty is-${seatDifficulty(state, player)}`}>
                {CPU_DIFFICULTY_LABELS[seatDifficulty(state, player)]}
              </span>{' '}
              CPU
            </span>
          ) : (
            who.name
          )}
          {isActive ? <span className="bb-lane-turn">Turn</span> : null}
          {isMine ? null : <span className="bb-deck-left">{who.hand.length} in hand</span>}
        </span>
        <div className="bb-piles">
          {[...leavingPiles.filter((pile) => !presentPiles(state).includes(pile)), ...presentPiles(state)].map(
            (pile) => {
              const top = pileCard(who, pile);
              const topBadge = top ? badgesMap.get(badgeOf(state, top)) : null;
              const removing = leavingPiles.includes(pile);
              const unlocked = isPileUnlocked(pile, state.round, battlePileCount(state), battleFirstPile(state));
              const disabled = isPileDisabled(state, pile);
              const releasing = !disabled && releasingPiles.includes(pile);
              const usable = unlocked && !disabled;
              const legal = usable && revealPiles.includes(pile);
              const canPlay =
                !cpuDrives(state, viewing) &&
                usable &&
                isMine &&
                state.activePlayer === viewing &&
                playables.includes(pile);
              const chooseTop =
                pending?.type === 'choose_board_badge' && pending.owner === player && pending.piles.includes(pile);
              const undoable = state.undo?.player === player && state.undo.pile === pile;
              const actionable =
                !cpuDrives(state, viewing) &&
                (undoable ||
                  (pending?.type === 'choose_pile' && pending.player === player && pending.piles.includes(pile)) ||
                  chooseTop ||
                  (!pending && (legal || canPlay)));
              const playFx = fxItems.find(
                (item) => PILE_LABEL_FX.has(item.kind) && item.player === player && item.pile === pile
              );
              const statFx = fxItems.filter(
                (item) => item.kind === 'stat' && item.player === player && item.pile === pile
              );
              const targetId = targetFx.find((item) => item.player === player && item.pile === pile)?.badgeId;
              // The badge normally still sits on its pile, so it keeps rendering in place; a copy only fills an emptied pile.
              const targeted = targetId != null;
              const targetBadge = targetId && !topBadge ? badgesMap.get(targetId) : null;
              const picking = legal || chooseTop;
              const ongoing =
                !disabled &&
                !!topBadge &&
                effectsForBadge(topBadge.id).some(
                  (effect) =>
                    effect.trigger === 'Ongoing' &&
                    effect.stat != null &&
                    (effect.ops.some((op) => op.kind === 'disable_piles' || op.kind === 'ignore_stat_lead')
                      ? hasStatLead(state, player, effect.stat, {
                          includeDisabledPiles: effect.ops.some((op) => op.kind === 'disable_piles'),
                        })
                      : hasStatLead(state, player, effect.stat) || ignoreStatLead)
                );
              return (
                <div
                  key={pile}
                  className={`bb-pile${disabled ? ' is-disabled' : releasing ? ' is-releasing' : !unlocked ? ' is-locked' : ''}${
                    ongoing && topBadge ? ` is-ongoing rarity-${topBadge.rarityName}` : ''
                  }${picking ? ' is-legal' : ''}${
                    chooseTop && losingBoardPick ? ' is-danger' : ''
                  }${playFx ? ' is-playing' : ''}${targeted ? ' is-targeted' : ''}${
                    isMine && revealDrag.dragging && hoverRevealPile === pile ? ' is-drop-target' : ''
                  }${removing ? ' is-removing' : ''}`}
                  data-bb-reveal-pile={isMine ? pile : undefined}
                  onAnimationEnd={
                    removing || releasing
                      ? (event) => {
                          if (event.target !== event.currentTarget) return;
                          if (removing) setLeavingPiles((current) => current.filter((item) => item !== pile));
                          if (releasing) setReleasingPiles((current) => current.filter((item) => item !== pile));
                        }
                      : undefined
                  }
                  onMouseEnter={legal ? () => setHoverRevealPile(pile) : undefined}
                  onMouseLeave={
                    legal ? () => setHoverRevealPile((current) => (current === pile ? null : current)) : undefined
                  }
                >
                  <PileRarityPips pile={pile} unlocked={unlocked} blockedRarities={blockedRevealRarities} />
                  {disabled || releasing ? (
                    <>
                      <span className={`bb-pile-play-label is-disabled${releasing ? ' is-releasing' : ''}`}>
                        Disabled
                      </span>
                      <PileDisableLasso releasing={releasing} />
                    </>
                  ) : null}
                  {playFx ? (
                    <React.Fragment key={playFx.key}>
                      <span
                        className={`bb-pile-play-ring is-${playFx.kind}${
                          playFx.revealedAndActivated ? ' is-reveal-play' : ''
                        }`}
                        aria-hidden
                        onAnimationEnd={(event) => {
                          if (event.target !== event.currentTarget) return;
                          dismissFx(playFx.key);
                        }}
                      />
                      <span
                        className={`bb-pile-play-label is-${playFx.kind}${
                          playFx.revealedAndActivated ? ' is-reveal-play' : ''
                        }`}
                        aria-hidden
                      >
                        {battleFxLabel(playFx)}
                      </span>
                    </React.Fragment>
                  ) : null}
                  {targeted && !playFx ? (
                    <span className="bb-pile-play-label is-targeted" aria-hidden>
                      Targeted
                    </span>
                  ) : null}
                  {statFx.length ? (
                    <span className="bb-stat-pops" aria-hidden>
                      {statFx.map((item) => (
                        <span key={item.key} className={`bb-stat-pop${(item.amount ?? 0) < 0 ? ' is-loss' : ''}`}>
                          {(item.amount ?? 0) > 0 ? '+' : ''}
                          {item.amount} {item.stat ? BADGE_STAT_LABELS[item.stat] : ''}
                        </span>
                      ))}
                    </span>
                  ) : null}
                  <div className="bb-pile-stack">
                    {targetBadge ? (
                      <div className="bb-pile-targeted">
                        <BattleBadge badge={targetBadge} compact />
                      </div>
                    ) : topBadge ? (
                      <BattleBadge
                        badge={topBadge}
                        compact
                        imageOnly={disabled}
                        coached={!disabled && coaching && ((canPlay && !revealPick) || legal || chooseTop)}
                        dimmed={disabled || (targeting && !chooseTop && !undoable)}
                        myBoard={disabled ? undefined : boardStats[player]}
                        foeBoard={disabled ? undefined : boardStats[opponentOf(player)]}
                        statBoost={
                          disabled || who.statBoostCardId !== top || who.statBoostTurnsLeft <= 0 ? null : who.statBoost
                        }
                        statGain={disabled || !top ? null : (cardStatGain(state, top) ?? null)}
                        onRevealFired={
                          disabled ||
                          !top ||
                          !effectsForBadge(topBadge.id).some((effect) => effect.trigger === 'On Reveal')
                            ? undefined
                            : (state.onRevealFired?.[top] ?? false)
                        }
                        ongoing={ongoing}
                        ignoreStatLead={!disabled && ignoreStatLead}
                        onClick={actionable ? () => onPile(player, pile) : undefined}
                      />
                    ) : usable ? (
                      <button
                        type="button"
                        className={`bb-pile-empty${actionable ? ' is-legal' : ''}${coaching && legal ? ' is-coached' : ''}`}
                        onClick={actionable ? () => onPile(player, pile) : undefined}
                        disabled={!actionable}
                      >
                        Empty
                      </button>
                    ) : disabled ? (
                      <div className="bb-pile-empty is-disabled" />
                    ) : (
                      <div className="bb-pile-empty is-locked">Unlocks on Turn {pileUnlockRound(pile)}</div>
                    )}
                  </div>
                </div>
              );
            }
          )}
          <LaneStats
            mine={boardStats[player]}
            theirs={boardStats[opponentOf(player)]}
            preview={preview}
            focusStat={focusStat}
          />
          <div className="bb-pile bb-deck-pile" aria-label={`Deck, ${who.deck.length} left`}>
            <span
              className="bb-pile-pips bb-stat-pile-label"
              aria-describedby={pure ? `bb-deck-pure-tip-${player}` : undefined}
            >
              Deck
              {pure ? (
                <span className="bb-deck-pure-tip" id={`bb-deck-pure-tip-${player}`} role="tooltip">
                  {PURE_DECK_HINT}
                </span>
              ) : null}
            </span>
            <div className="bb-pile-stack">
              <div className={`bb-deck-stack${who.deck.length ? '' : ' is-empty'}`}>
                <BattleCardBack />
                <span className={`bb-deck-count${who.deck.length <= 1 ? ' is-low' : ''}`}>{who.deck.length}</span>
              </div>
              {isMine ? (
                <DeckRarityPips badgeIds={who.deck.map((cardId) => badgeOf(state, cardId))} badgesMap={badgesMap} />
              ) : null}
              {isMine ? (
                <HoverTip text={who.hand.length >= BATTLE_MAX_HAND ? HAND_FULL_HINT : null}>
                  <button
                    type="button"
                    className={`bb-draw${coaching && canDraw(state) && humanTurn && allowed({ type: 'draw' }) ? ' is-coached' : ''}`}
                    disabled={!canDraw(state) || !!pending || !humanTurn || !allowed({ type: 'draw' })}
                    onClick={() => apply({ type: 'draw' })}
                  >
                    Draw
                  </button>
                </HoverTip>
              ) : null}
              <BattleFxCluster
                items={overlayFor(player, 'deck')}
                zone="deck"
                viewing={viewing}
                badgesMap={badgesMap}
                onDone={dismissFx}
              />
            </div>
          </div>
          <div
            className="bb-pile bb-discard-pile"
            aria-label={discarded.length ? `Discard pile, ${discarded.length}` : 'Discard pile'}
          >
            <span className="bb-pile-pips bb-stat-pile-label">Discard pile</span>
            <div className="bb-pile-stack">
              {discarded.length ? (
                <div className="bb-discard-fan">
                  {discarded.map((cardId, index) => {
                    const badge = badgesMap.get(badgeOf(state, cardId));
                    if (!badge) return null;
                    return (
                      <div key={cardId} className="bb-discard-card" style={discardCardStyle(index, discarded.length)}>
                        <BattleBadge badge={badge} grayscale compact />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bb-pile-empty bb-discard-empty" />
              )}
              <BattleFxCluster
                items={overlayFor(player, 'discard')}
                zone="discard"
                viewing={viewing}
                badgesMap={badgesMap}
                onDone={dismissFx}
              />
            </div>
          </div>
          <BattleFxCluster
            items={overlayFor(player, 'board')}
            zone="board"
            viewing={viewing}
            badgesMap={badgesMap}
            onDone={dismissFx}
          />
        </div>
      </div>
    );
  };

  const myHand = state.players[viewing].hand;
  const drawnHandIds = [
    ...state.fx.filter((item) => item.kind === 'draw' && item.player === viewing).map((item) => item.cardId),
    ...fxItems.filter((item) => item.kind === 'draw' && item.player === viewing).map((item) => item.cardId),
  ].filter((cardId, index, all) => all.indexOf(cardId) === index);
  const waitingDrawIds = new Set(
    (liveState ?? state).fx
      .filter((item) => item.kind === 'draw' && item.player === viewing && !drawnHandIds.includes(item.cardId))
      .map((item) => item.cardId)
  );
  const previewRevealBoards = (cardId: string) => handPreviewRevealBoards(state, viewing, cardId);
  const foeHandCount = state.players[foe].hand.length;
  const cpuPlaysMe = cpuDrives(state, viewing);
  const cpuPlayingCue = cpuPlaysMe ? `${cpuName(seatDifficulty(state, viewing))} playing` : '';
  const humanTurn = isMyTurnToAct(state, viewing) && !cpuPlaysMe;
  const actionCue = cpuPlayingCue
    ? cpuPlayingCue
    : pending
      ? battleActionPrompt(state, viewing)
      : revealPick
        ? 'Choose a pile to reveal onto.'
        : humanTurn
          ? humanActionCue(state.actionsLeft, scheduledActionsLeft(state, viewing), viewerOpens(state, viewing))
          : '';
  const foeOpens = opponentOpens(state, viewing);
  const activeName = state.players[state.activePlayer].name;
  const committedState = committed?.state ?? liveState ?? state;
  const foeTurnOpen = shownTurnStillOpen(state, committedState);
  const foeActionsLeft = foeTurnOpen ? committedState.actionsLeft : state.actionsLeft;
  const actionsLabel = `${foeActionsLeft} more action${foeActionsLeft === 1 ? '' : 's'}`;
  const foeActionCue =
    state.phase === 'turn' && state.activePlayer !== viewing
      ? foeOpens
        ? `${activeName} goes first with ${BATTLE_OPENING_ACTIONS} action.`
        : foeTurnOpen
          ? `${activeName} has ${actionsLabel}`
          : `${activeName} playing`
      : '';
  const cueText = actionCue || foeActionCue;
  const coach = preset?.coach && liveState ? preset.coach(liveState) : null;

  const renderCpuReact = (player: PlayerId) => {
    const held = heldCpuEmotes[player];
    if (!held) return null;
    const debugScore = process.env.NODE_ENV === 'development';
    const scoreState = liveState ?? state;
    const gap = debugScore && scoreState ? faceScore(scoreState, player) : null;
    const gapLabel = gap == null ? '' : ` ${gap > 0 ? '+' : ''}${Number(gap.toFixed(2))}`;
    return (
      <HoverTip
        key={held.emoji}
        className="bb-cpu-react"
        text={`${cpuEmoteHover(cpuName(seatDifficulty(state, player)), held.face)}${gapLabel}`}
      >
        <span aria-hidden>{held.emoji}</span>
      </HoverTip>
    );
  };

  const toggleFullscreen = () => {
    const page = pageRef.current;
    if (!page) return;
    if (activeFullscreen() === page) {
      exitBattleFullscreen();
      return;
    }
    if (isFullscreen) {
      setIsFullscreen(false);
      return;
    }
    enterBattleFullscreen(page);
  };

  return (
    <div className={`bb-page is-playing${revealDrag.active ? ' is-reveal-dragging' : ''}`} ref={bindBattlePage}>
      {isFullscreen ? (
        <BattleField enableFlash={false} className="login-badge-backdrop--app bb-fullscreen-field" />
      ) : null}
      {coach ? <div className="bb-coach">{coach}</div> : null}
      {revealDrag.ghost ? (
        <div
          ref={revealDrag.bindGhost}
          className={`bb-reveal-ghost${revealDrag.ghost.returning ? ' is-returning' : ''}`}
          style={{ width: revealDrag.ghost.width, height: revealDrag.ghost.height }}
          aria-hidden
        >
          <BattleBadge
            badge={revealDrag.ghost.badge}
            selected={revealPick === revealDrag.ghost.cardId}
            myBoard={boardStats[viewing]}
            foeBoard={boardStats[foe]}
            statGain={cardStatGain(state, revealDrag.ghost.cardId) ?? null}
            revealBoards={previewRevealBoards(revealDrag.ghost.cardId)}
            ignoreStatLead={ignoreStatLead}
            compact
          />
        </div>
      ) : null}
      <BattlePageHeader
        title={preset?.title}
        subtitle={
          preset ? `${preset.label} · Turn ${state.round}` : `Turn ${state.round} · first to ${prestigeToWin(state)}`
        }
        subtitleClassName="bb-kicker"
        actions={
          <>
            <button type="button" className="bb-log-btn" onClick={() => setLogOpen(true)}>
              Log
            </button>
            {state.phase === 'game_over' && hideResult && !preset ? (
              <button type="button" className="bb-draw" onClick={roomId ? rematch : () => startMatch()}>
                Rematch
              </button>
            ) : null}
            <button type="button" className="bb-resign" onClick={requestLeaveMatch}>
              {preset
                ? preset.forfeitOnLeave && state.phase === 'game_over'
                  ? 'Continue'
                  : (preset.exitLabel ?? 'Exit')
                : state.phase === 'game_over'
                  ? 'Change decks'
                  : 'Leave battle'}
            </button>
            <button type="button" className="bb-fullscreen" aria-pressed={isFullscreen} onClick={toggleFullscreen}>
              {isFullscreen ? 'Minimize' : 'Full screen'}
            </button>
          </>
        }
      />

      <div className="bb-board">
        <div className="bb-board-main">
          <div className="bb-foe-seat">
            {renderCpuReact(foe)}
            <div
              className={`bb-foe-hand${foeHandCount ? '' : ' is-empty'}${
                state.phase !== 'game_over' && state.activePlayer === foe ? ' is-turn' : ''
              }`}
              aria-label={`${foeHandCount} badges in hand`}
            >
              {foeHandCount
                ? Array.from({ length: foeHandCount }, (_, index) => (
                    <div key={index} className="bb-foe-card" style={foeCardStyle(index, foeHandCount)}>
                      <BattleCardBack />
                    </div>
                  ))
                : 'Empty'}
              <BattleFxCluster
                items={overlayFor(foe, 'hand')}
                zone="hand"
                viewing={viewing}
                badgesMap={badgesMap}
                onDone={dismissFx}
              />
            </div>
          </div>
          <div className="bb-lanes">
            <div className="bb-scores">
              <div className="bb-score-slot">
                {renderActionsLeft(foe)}
                {renderPrestige(foe)}
                {renderInstantWinHint(foe)}
              </div>
              <div
                className={`bb-turns${prestigeRaceIsClosing(state) ? ' is-last' : ''}`}
                aria-label={
                  state.phase === 'game_over'
                    ? resultTitle
                    : prestigeRaceIsClosing(state)
                      ? 'Last turn'
                      : `Turn ${state.round}${
                          foeOpens
                            ? `, ${activeName} goes first`
                            : state.activePlayer === viewing
                              ? cpuPlayingCue
                                ? `, ${cpuPlayingCue}`
                                : ', your turn'
                              : foeTurnOpen
                                ? `, ${activeName} has ${actionsLabel}`
                                : `, ${activeName} playing`
                        }`
                }
              >
                {state.phase !== 'game_over' ? (
                  <span className={`bb-turn-pill${state.activePlayer === viewing ? ' is-you' : ' is-foe'}`}>
                    {foeOpens
                      ? 'Goes first'
                      : cpuPlaysMe && state.activePlayer === viewing
                        ? cpuPlayingCue
                        : state.activePlayer === viewing
                          ? 'Your turn'
                          : foeTurnOpen
                            ? `${foeActionsLeft} action${foeActionsLeft === 1 ? '' : 's'}`
                            : 'Playing'}
                  </span>
                ) : null}
                {state.phase === 'game_over' ? (
                  <span className={`bb-turns-result${state.winner === 'tie' ? ' is-draw' : ''}`}>
                    <span className="bb-turns-result-kicker">{state.winner === 'tie' ? 'Game over' : '🏆 Winner'}</span>
                    <strong className="bb-turns-result-name">
                      {state.winner === 'tie' ? 'Draw' : state.players[state.winner ?? 0].name}
                    </strong>
                  </span>
                ) : prestigeRaceIsClosing(state) ? (
                  <strong className="bb-turns-val">LAST TURN</strong>
                ) : (
                  <strong className="bb-turns-val">Turn {state.round}</strong>
                )}
              </div>
              <div className="bb-score-slot">
                {renderPrestige(viewing)}
                {renderInstantWinHint(viewing)}
                {renderActionsLeft(viewing)}
              </div>
            </div>
            {renderPiles(foe)}
            {renderPiles(viewing)}
          </div>
          <div className="bb-hand-row">
            {pending?.player === viewing && pending.type === 'choose_rarity' ? (
              <div className="bb-choice-row">
                {pending.rarities.map((rarity) => (
                  <button
                    key={rarity}
                    type="button"
                    className="bb-filter-pill"
                    onClick={() => apply({ type: 'resolve', target: { kind: 'rarity', rarity } })}
                  >
                    {RARITY_TIER_LABELS[rarity]}
                  </button>
                ))}
              </div>
            ) : null}
            {pending?.player === viewing && pending.type === 'choose_swap_drawn' ? (
              <div className="bb-choice-row">
                <button
                  type="button"
                  className="bb-filter-pill"
                  onClick={() => apply({ type: 'resolve', target: { kind: 'swap_drawn', swap: false } })}
                >
                  Keep
                </button>
                <button
                  type="button"
                  className="bb-filter-pill"
                  onClick={() => apply({ type: 'resolve', target: { kind: 'swap_drawn', swap: true } })}
                >
                  Exchange
                </button>
              </div>
            ) : null}
            {pending?.player === viewing && pending.type === 'choose_stat' ? (
              <div className="bb-choice-row">
                {pending.stats.map((stat) => (
                  <button
                    key={stat}
                    type="button"
                    className="bb-filter-pill"
                    onClick={() => apply({ type: 'resolve', target: { kind: 'stat', stat } })}
                  >
                    {BADGE_STAT_LABELS[stat]}
                  </button>
                ))}
              </div>
            ) : null}
            {pending?.player === viewing && pending.type === 'choose_deck_card' ? (
              <div className="bb-choice-row">
                {pending.cardIds.map((cardId) => {
                  const badge = badgesMap.get(badgeOf(state, cardId));
                  if (!badge) return null;
                  return (
                    <BattleBadge
                      key={cardId}
                      badge={badge}
                      statGain={cardStatGain(state, cardId) ?? null}
                      compact
                      onClick={() => apply({ type: 'resolve', target: { kind: 'deck_card', cardId } })}
                    />
                  );
                })}
              </div>
            ) : null}
            <div className="bb-hand-cue-row">
              <p className={`bb-hand-cue${cueText ? '' : ' is-quiet'}${coachOverview ? ' is-coached' : ''}`}>
                {cueText}
              </p>
            </div>
            <div
              className={`bb-hand${myHand.length ? '' : ' is-empty'}${
                state.phase !== 'game_over' && state.activePlayer === viewing ? ' is-turn' : ''
              }${revealDrag.active && myHand.length === 1 ? ' is-return-hold' : ''}`}
              aria-label={`Your hand, ${myHand.length} badges`}
            >
              {renderCpuReact(viewing)}
              {myHand.length
                ? myHand.map((cardId) => {
                    if (presenting && waitingDrawIds.has(cardId)) return null;
                    const badge = badgesMap.get(badgeOf(state, cardId));
                    if (!badge) return null;
                    const piles = cardLegalPiles(state, cardId, state.round);
                    const rarityLocked = piles.length === 0;
                    const opponentsTurn = state.phase !== 'game_over' && !humanTurn;
                    const choosable =
                      !cpuPlaysMe &&
                      ((pending?.type === 'choose_hand' && pending.cardIds.includes(cardId)) ||
                        (pending?.type === 'choose_pile' && !!pending.options?.includes(cardId)));
                    const actingWith =
                      revealPick === cardId || (pending?.type === 'choose_pile' && pending.cardId === cardId);
                    const unavailable = (rarityLocked || opponentsTurn) && !actingWith && !choosable;
                    const canReveal =
                      !cpuPlaysMe &&
                      !rarityLocked &&
                      !pending &&
                      humanTurn &&
                      (!coaching || revealable.some((item) => item.cardId === cardId));
                    const drawnAt = drawnHandIds.indexOf(cardId);
                    const justDrawn = drawnAt >= 0;
                    return (
                      <div
                        key={cardId}
                        ref={revealDrag.bindSlot(cardId)}
                        className={`${revealDrag.slotClassName(cardId, canReveal)}${justDrawn ? ' is-drawn' : ''}`}
                        style={{
                          ...revealDrag.slotStyle(cardId),
                          ...(justDrawn ? { '--bb-hand-draw-i': drawnAt } : null),
                        }}
                        onPointerDown={
                          canReveal ? (event) => revealDrag.onPointerDown(event, cardId, badge) : undefined
                        }
                        onDragStart={(event) => event.preventDefault()}
                      >
                        {justDrawn ? (
                          <span className="bb-hand-draw-label" aria-hidden>
                            Drew
                          </span>
                        ) : null}
                        <BattleBadge
                          badge={badge}
                          selected={actingWith}
                          legal={choosable && !actingWith}
                          coached={coaching && canReveal && !actingWith}
                          danger={pending?.type === 'choose_hand' && LOSING_HAND_PICKS.has(pending.purpose)}
                          locked={unavailable}
                          dimmed={unavailable}
                          myBoard={boardStats[viewing]}
                          foeBoard={boardStats[foe]}
                          statGain={cardStatGain(state, cardId) ?? null}
                          revealBoards={previewRevealBoards(cardId)}
                          ignoreStatLead={ignoreStatLead}
                          compact
                          onClick={
                            canReveal || choosable
                              ? () => {
                                  if (revealDrag.didDrag(cardId)) return;
                                  onHandCard(viewing, cardId);
                                }
                              : undefined
                          }
                        />
                      </div>
                    );
                  })
                : 'Empty'}
              <BattleFxCluster
                items={overlayFor(viewing, 'hand')}
                zone="hand"
                viewing={viewing}
                badgesMap={badgesMap}
                onDone={dismissFx}
              />
            </div>
            {myHand.length === BATTLE_MAX_HAND - 1 ? (
              <p className="bb-hand-cue">Max hand size: {BATTLE_MAX_HAND}</p>
            ) : myHand.length >= BATTLE_MAX_HAND ? (
              <p className="bb-hand-cue">{HAND_FULL_HINT}</p>
            ) : null}
          </div>
        </div>
      </div>

      {leaveConfirmOpen ? (
        <div className="bb-overlay" onClick={() => setLeaveConfirmOpen(false)}>
          <div
            className="bb-modal"
            role="dialog"
            aria-label={preset?.leaveConfirm?.title ?? 'Leave battle'}
            onClick={(event) => event.stopPropagation()}
          >
            <h2>{preset?.leaveConfirm?.title ?? 'Leave battle'}</h2>
            <p>{preset?.leaveConfirm?.body ?? 'Are you sure you want to leave this battle?'}</p>
            <button
              type="button"
              onClick={() => {
                setLeaveConfirmOpen(false);
                if (preset?.forfeitOnLeave && liveState && liveState.phase !== 'game_over') {
                  concedeMatch();
                  return;
                }
                leaveMatch();
              }}
              autoFocus
            >
              {preset?.leaveConfirm?.confirm ?? 'Leave'}
            </button>
            <button type="button" className="is-ghost" onClick={() => setLeaveConfirmOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {logOpen ? (
        <div className="bb-overlay is-log" onClick={() => setLogOpen(false)}>
          <div
            className="bb-modal bb-log-modal"
            role="dialog"
            aria-label="Battle log"
            onClick={(event) => event.stopPropagation()}
          >
            <h2>Log</h2>
            {state.log.length ? (
              <div className="bb-log">
                <BattleLogList entries={state.log} cards={state.cards} />
              </div>
            ) : (
              <p>Nothing has happened yet.</p>
            )}
            <button type="button" className="is-ghost" onClick={() => setLogOpen(false)}>
              Close
            </button>
          </div>
        </div>
      ) : null}

      {state.phase === 'game_over' && !hideResult && !preset?.holdResult?.(liveState ?? state) ? (
        <div className="bb-overlay" onClick={() => setHideResult(true)}>
          <div
            className="bb-modal"
            role="dialog"
            aria-label="Battle result"
            onClick={(event) => event.stopPropagation()}
          >
            <h2>{resultTitle}</h2>
            {state.concededBy != null ? (
              <p className="bb-modal-sub">{state.players[state.concededBy].name} conceded the battle</p>
            ) : !roomId ? (
              <p className="bb-modal-sub">
                against{' '}
                {state.winner === 'tie' || state.winner == null
                  ? state.players[foe].name
                  : state.players[opponentOf(state.winner)].name}
              </p>
            ) : null}
            <p>
              Prestige {state.players[0].prestige}–{state.players[1].prestige}
            </p>
            {preset ? (
              preset.resultActions(state, restartPreset)
            ) : (
              <>
                <button type="button" onClick={roomId ? rematch : () => startMatch()}>
                  Rematch
                </button>
                <button type="button" className="is-ghost" onClick={leaveMatch}>
                  Change decks
                </button>
                <button type="button" className="is-ghost" onClick={() => setHideResult(true)}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const BadgesBattleView: React.FC<Props> = (props) => (
  <BattleOpenBadgeProvider onOpenBadge={props.onSelectBadge}>
    <BadgesBattleScreen {...props} />
  </BattleOpenBadgeProvider>
);
